import React, { useState, useEffect, useRef } from 'react';
import { apiFetch, getApiBaseUrl } from '../../../utils/apiConfig';
import { getAuthHeaders } from '../../../utils/authUtils';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '../../CentralSnackbar';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Autocomplete,
  Radio,
  RadioGroup,
  FormLabel,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Preview as PreviewIcon,
  Settings as SettingsIcon,
  Remove as RemoveIcon,
  ContentCopy as CopyIcon,
  FolderOpen as GroupIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
  Description as PdfIcon,
  VisibilityOff as VisibilityOffIcon,
  Visibility as VisibilityIcon,
  AccountTreeOutlined as StructuredIcon
} from '@mui/icons-material';

import { exportTemplateToCSV, exportMultipleTemplatesToCSV, parseCSVToTemplate, validateTemplate } from '../../../utils/csvExporter';
import { downloadCSVGuideWithData, generateCSVGuide } from '../../../utils/csvGuideGenerator';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:25000';

// Vollständige DICOM-Modalitätenliste aus CalendarSettings.js (ohne IDs)
const ALL_MODALITIES_RAW = [
  // Standard-Modalitäten
  { code: 'DX', name: 'Digitale Radiographie' },
  { code: 'CT', name: 'Computertomographie' },
  { code: 'MR', name: 'Magnetresonanztomographie' },
  { code: 'RF', name: 'Radiofluoroskopie' },
  { code: 'XA', name: 'Röntgen-Angiographie' },
  { code: 'MG', name: 'Mammographie' },
  { code: 'US', name: 'Ultraschall' },
  // Erweiterte Modalitäten (alphabetisch)
  { code: 'AR', name: 'Autorefraktion' },
  { code: 'ASMT', name: 'Inhaltsbewertungsergebnisse' },
  { code: 'AU', name: 'Audio' },
  { code: 'BDUS', name: 'Knochendichtemessung (Ultraschall)' },
  { code: 'BI', name: 'Biomagnetische Bildgebung' },
  { code: 'BMD', name: 'Knochendichtemessung (Röntgen)' },
  { code: 'CR', name: 'Computed Radiography' },
  { code: 'CTPROTOCOL', name: 'CT-Protokoll (durchgeführt)' },
  { code: 'DG', name: 'Diaphanographie' },
  { code: 'DOC', name: 'Dokument' },
  { code: 'ECG', name: 'Elektrokardiographie' },
  { code: 'EPS', name: 'Kardiale Elektrophysiologie' },
  { code: 'ES', name: 'Endoskopie' },
  { code: 'FID', name: 'Referenzmarker' },
  { code: 'GM', name: 'Allgemeine Mikroskopie' },
  { code: 'HC', name: 'Hardcopy' },
  { code: 'HD', name: 'Hämodynamische Wellenform' },
  { code: 'IO', name: 'Intraorale Radiographie' },
  { code: 'IOL', name: 'Intraokuläre Linsendaten' },
  { code: 'IVOCT', name: 'Intravaskuläre optische Kohärenztomographie' },
  { code: 'IVUS', name: 'Intravaskulärer Ultraschall' },
  { code: 'KER', name: 'Keratometrie' },
  { code: 'KO', name: 'Schlüsselobjektauswahl' },
  { code: 'LEN', name: 'Linsenmessung' },
  { code: 'LS', name: 'Laser-Oberflächenscan' },
  { code: 'M3D', name: 'Modell für 3D-Fertigung' },
  { code: 'NM', name: 'Nuklearmedizin' },
  { code: 'OAM', name: 'Ophthalmische Axialmessungen' },
  { code: 'OCT', name: 'Optische Kohärenztomographie (nicht-ophthalmisch)' },
  { code: 'OP', name: 'Ophthalmische Fotographie' },
  { code: 'OPM', name: 'Ophthalmische Kartierung' },
  { code: 'OPT', name: 'Ophthalmische Tomographie' },
  { code: 'OPTBSV', name: 'Ophthalmische Tomographie B-Scan-Volumenanalyse' },
  { code: 'OPTENF', name: 'Ophthalmische Tomographie En Face' },
  { code: 'OPV', name: 'Ophthalmisches Gesichtsfeld' },
  { code: 'OSS', name: 'Optischer Oberflächenscan' },
  { code: 'OT', name: 'Andere' },
  { code: 'PLAN', name: 'Plan' },
  { code: 'PR', name: 'Präsentationsstatus' },
  { code: 'PT', name: 'Positronen-Emissions-Tomographie' },
  { code: 'PX', name: 'Panorama-Röntgen' },
  { code: 'REG', name: 'Registrierung' },
  { code: 'RESP', name: 'Atemwellenform' },
  { code: 'RG', name: 'Radiographische Bildgebung (konventioneller Film/Bildschirm)' },
  { code: 'RTDOSE', name: 'Strahlentherapie-Dosis' },
  { code: 'RTIMAGE', name: 'Strahlentherapie-Bild' },
  { code: 'RTINTENT', name: 'Strahlentherapie-Absicht' },
  { code: 'RTPLAN', name: 'Strahlentherapie-Plan' },
  { code: 'RTRAD', name: 'RT-Strahlung' },
  { code: 'RTRECORD', name: 'RT-Behandlungsprotokoll' },
  { code: 'RTSEGANN', name: 'Strahlentherapie-Segmentannotation' },
  { code: 'RTSTRUCT', name: 'Strahlentherapie-Struktursatz' },
  { code: 'RWV', name: 'Real-World-Wert-Karte' },
  { code: 'SEG', name: 'Segmentierung' },
  { code: 'SM', name: 'Objektträger-Mikroskopie' },
  { code: 'SMR', name: 'Stereometrische Beziehung' },
  { code: 'SR', name: 'SR-Dokument' },
  { code: 'SRF', name: 'Subjektive Refraktion' },
  { code: 'STAIN', name: 'Automatischer Objektträger-Färber' },
  { code: 'TG', name: 'Thermographie' },
  { code: 'VA', name: 'Sehschärfe' },
  { code: 'XC', name: 'Externe Kamera-Fotographie' }
];

// Automatische ID-Generierung für Modalitäten (Index + 1)
const ALL_MODALITIES = ALL_MODALITIES_RAW.map((modality, index) => ({
  ...modality,
  id: index + 1
}));

// Verfügbare Units für strukturierte Findings
const AVAILABLE_UNITS = [
  { value: 'mm', label: 'mm' },
  { value: 'cm', label: 'cm' },
  { value: 'ml/m²', label: 'ml/m²' },
  { value: '%', label: '%' },
  { value: 'g/m²', label: 'g/m²' },
  { value: 'MS', label: 'MS' },
  { value: 'custom', label: 'admin.reportingManagement.structuredReporting.fieldEditor.customUnit' }
];

// Finding-Typen
const FINDING_TYPES = [
  { value: 'freetext', label: 'admin.reportingManagement.structuredReporting.fieldEditor.types.freetext' },
  { value: 'freetext_unit', label: 'admin.reportingManagement.structuredReporting.fieldEditor.types.freetextWithUnit' },
  { value: 'dropdown', label: 'admin.reportingManagement.structuredReporting.fieldEditor.types.dropdown' },
  { value: 'checkboxes', label: 'admin.reportingManagement.structuredReporting.fieldEditor.types.checkboxes' },
  { value: 'yesno_comment', label: 'admin.reportingManagement.structuredReporting.fieldEditor.types.yesNoComment' },
  { value: 'database', label: 'admin.reportingManagement.structuredReporting.fieldEditor.types.database' }
];

const StructuredReporting = () => {
  const { t } = useTranslation();
  const { showSuccess, showError, showWarning } = useSnackbar();

  // Verfügbare Datenbankfelder aus taskColumns (ohne Blacklist)
  const DATABASE_FIELDS = [
    { key: '', label: t('admin.reportingManagement.structuredReporting.fieldEditor.noLink') },
    { key: 'patient_id', label: t('taskColumns.patient_id') },
    { key: 'patient_name', label: t('taskColumns.patient_name') },
    { key: 'birth_date', label: t('taskColumns.birth_date') },
    { key: 'sex', label: t('taskColumns.sex') },
    { key: 'modality', label: t('taskColumns.modality') },
    { key: 'study_description', label: t('taskColumns.study_description') },
    { key: 'clinical_info', label: t('taskColumns.clinical_info') },
    { key: 'question', label: t('taskColumns.question') },
    { key: 'accession_number', label: t('taskColumns.accession_number') },
    { key: 'referring_physician', label: t('taskColumns.referring_physician') },
    { key: 'admission_id', label: t('taskColumns.admission_id') },
    { key: 'comment', label: t('taskColumns.comment') },
    { key: 'scan_protocol', label: t('taskColumns.scan_protocol') },
    { key: 'first_reader', label: t('taskColumns.first_reader') },
    { key: 'patientsAge', label: t('taskColumns.patientsAge') },
    { key: 'patientsWeight', label: t('taskColumns.patientsWeight') },
    { key: 'patientsSize', label: t('taskColumns.patientsSize') },
    { key: 'occupation', label: t('taskColumns.occupation') },
    { key: 'additionalPatientsHistory', label: t('taskColumns.additionalPatientsHistory') },
    { key: 'admittingDiagnosesDescription', label: t('taskColumns.admittingDiagnosesDescription') }
  ];

  const [templates, setTemplates] = useState([]);
  const [examinations, setExaminations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorError, setEditorError] = useState(''); // Lokaler Fehler nur für Dialog-Validierung
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [showInactive, setShowInactive] = useState(false); // Show/hide inactive templates

  // Editor-States
  const [editorOpen, setEditorOpen] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    template_type: 'S', // S für Structured
    standard_for: { type: 'all', values: [] },
    fields_json: [], // Normale Felder (Freetext/Database) - DEPRECATED
    structured_json: { // Strukturierte Findings - DEPRECATED
      groups: [],
      findings: []
    }
  });

  // Einheitliche chronologische Liste aller Elemente
  const [allItems, setAllItems] = useState([]);

  // Standard-für States
  const [standardFor, setStandardFor] = useState('all'); // 'all', 'modalities', 'examinations'
  const [selectedValues, setSelectedValues] = useState([]);
  const [modalityFilter, setModalityFilter] = useState(''); // Filter für Untersuchungen nach Modalität

  // Field Editor States (für normale Felder)
  const [fieldEditorOpen, setFieldEditorOpen] = useState(false);
  const [currentField, setCurrentField] = useState(null);
  const [currentFieldIndex, setCurrentFieldIndex] = useState(-1);
  const [fieldForm, setFieldForm] = useState({
    id: '',
    label: '',
    type: 'freetext', // 'freetext' oder 'database'
    database_field: '',
    width: 12,
    height: 3,
    snippet: '' // Snippet-Kurzname (z.B. "@snippet_name")
  });

  // Structured Finding Editor States
  const [structuredEditorOpen, setStructuredEditorOpen] = useState(false);
  const [currentStructuredItem, setCurrentStructuredItem] = useState(null);
  const [currentStructuredIndex, setCurrentStructuredIndex] = useState(-1);
  const [structuredEditType, setStructuredEditType] = useState(''); // 'group' oder 'finding'
  const [structuredForm, setStructuredForm] = useState({
    type: 'group', // 'group' oder 'finding'
    name: '', // für Groups: Gruppenname; für Findings: Title
    subtitle: '', // nur für Findings
    finding_type: 'freetext', // für Findings
    placeholder: '', // für Freetext
    unit: '', // für Freetext mit Unit
    custom_unit: '', // falls unit === 'custom'
    options: [], // für Dropdown/Checkboxes (Array von Strings)
    comment_placeholder: '', // für Yes/No mit Kommentar
    database_field: '', // für Datenbankfeld-Findings
    group_id: null // für Findings: zu welcher Gruppe gehören sie (null = ohne Gruppe)
  });

  // Refs für Option-Inputs
  const optionInputRefs = useRef([]);
  const [focusOptionIndex, setFocusOptionIndex] = useState(-1);

  // CSV Import/Export States
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importErrors, setImportErrors] = useState([]);

  // HTML Import States
  const [htmlImportDialogOpen, setHtmlImportDialogOpen] = useState(false);
  const [htmlImportFiles, setHtmlImportFiles] = useState([]);
  const [htmlImportLoading, setHtmlImportLoading] = useState(false);
  const [htmlImportResults, setHtmlImportResults] = useState(null);

  // Numpad Navigation
  const [numpadMode, setNumpadMode] = useState(false);
  const [focusedFindingIndex, setFocusedFindingIndex] = useState(0);
  const [previewValues, setPreviewValues] = useState({});
  const [dropdownStates, setDropdownStates] = useState({});
  const findingRefs = useRef([]);

  // Preview States
  const [previewOpen, setPreviewOpen] = useState(false);

  // Snippets States
  const [availableSnippets, setAvailableSnippets] = useState([]);
  const [snippetsLoading, setSnippetsLoading] = useState(false);

  // Resize functionality
  const [isResizing, setIsResizing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  // Drag and Drop States für Hauptelemente
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);
  
  // Drag and Drop States für Findings innerhalb von Gruppen
  const [groupDraggedIndex, setGroupDraggedIndex] = useState(null);
  const [groupDropIndex, setGroupDropIndex] = useState(null);
  const [activeGroupId, setActiveGroupId] = useState(null);
  
  // Gruppen Ein-/Ausklappen State
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const resizeData = useRef({
    fieldIndex: -1,
    direction: null, // 'width', 'height'
    startPos: { x: 0, y: 0 },
    startSize: { width: 0, height: 0 }
  });

  useEffect(() => {
    fetchTemplates();
    fetchExaminations();
    fetchSnippets();
  }, [showInactive]); // Re-fetch when showInactive changes

  // Cleanup für Resize Event Listeners - EXAKT wie FreetextReporting
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, []);

  // Numpad Event Listener
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!numpadMode) return;

      const { key } = event;
      
      // Navigation zwischen Findings
      if (key === 'Enter' || key === 'ArrowDown') {
        event.preventDefault();
        navigateToNextFinding();
      } else if (key === 'ArrowUp') {
        event.preventDefault();
        navigateToPreviousFinding();
      } else if (key >= '1' && key <= '9') {
        event.preventDefault();
        selectFindingOption(parseInt(key));
      }
    };

    // Event-Capturing verwenden, um Events vor Input-Feldern abzufangen
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [numpadMode, focusedFindingIndex]);

  // Auto-Focus beim Aktivieren des Numpad-Modus
  useEffect(() => {
    if (numpadMode && getAllFindings().length > 0) {
      setFocusedFindingIndex(0);
      setTimeout(() => focusCurrentFinding(0), 100);
    }
  }, [numpadMode]);

  // Auto-Focus erste Option bei Dropdown/Checkboxes
  useEffect(() => {
    if (structuredEditorOpen && 
        (structuredForm.finding_type === 'dropdown' || structuredForm.finding_type === 'checkboxes') &&
        structuredForm.options.length > 0) {
      setFocusOptionIndex(0);
    }
  }, [structuredForm.finding_type, structuredForm.options.length, structuredEditorOpen]);

  // Fokussiere Option basierend auf focusOptionIndex
  useEffect(() => {
    if (focusOptionIndex >= 0 && focusOptionIndex < structuredForm.options.length) {
      setTimeout(() => {
        if (optionInputRefs.current[focusOptionIndex]) {
          optionInputRefs.current[focusOptionIndex].focus();
        }
      }, 100);
      setFocusOptionIndex(-1); // Reset nach Fokussierung
    }
  }, [focusOptionIndex, structuredForm.options.length]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      // Füge include_inactive Parameter hinzu
      const url = showInactive ? '/templates?include_inactive=true' : '/templates';
      const response = await apiFetch(url);
      
      if (response.ok) {
        const data = await response.json();
        // Filter für strukturierte Templates (Type 'S')
        const structuredTemplates = data.filter(t => t.template_type === 'S');
        setTemplates(structuredTemplates);
      } else {
        console.error('Failed to load templates');
        showError(t('admin.reportingManagement.structuredReporting.errors.fetchTemplates'), 'ERR-STRUCTURED-001');
      }
    } catch (err) {
      console.error('Network error loading templates:', err);
      showError(t('admin.reportingManagement.structuredReporting.errors.fetchTemplates'), 'ERR-STRUCTURED-002');
    } finally {
      setLoading(false);
    }
  };

  const fetchExaminations = async () => {
    try {
      const response = await apiFetch('/admin/examinations');
      
      if (response.ok) {
        const data = await response.json();
        setExaminations(data);
      }
    } catch (err) {
      console.error('Error fetching examinations:', err);
    }
  };

  const fetchSnippets = async () => {
    setSnippetsLoading(true);
    try {
      const response = await apiFetch('/snippets');
      if (response.ok) {
        const data = await response.json();
        setAvailableSnippets(data);
        console.log('✅ Snippets geladen:', data.length);
      }
    } catch (e) {
      console.error('Fehler beim Laden der Snippets:', e);
    } finally {
      setSnippetsLoading(false);
    }
  };

  // Numpad Navigation Functions
  const navigateToNextFinding = () => {
    const totalFindings = getAllFindings().length;
    if (totalFindings === 0) return;

    const nextIndex = (focusedFindingIndex + 1) % totalFindings;
    setFocusedFindingIndex(nextIndex);
    focusCurrentFinding(nextIndex);
  };

  const navigateToPreviousFinding = () => {
    const totalFindings = getAllFindings().length;
    if (totalFindings === 0) return;

    const prevIndex = focusedFindingIndex <= 0 ? totalFindings - 1 : focusedFindingIndex - 1;
    setFocusedFindingIndex(prevIndex);
    focusCurrentFinding(prevIndex);
  };

  const focusCurrentFinding = (index) => {
    const allFindings = getAllFindings();
    const currentFinding = allFindings[index];
    
    if (!currentFinding || !findingRefs.current[index]) return;

    // Auto-Focus je nach Finding-Typ
    setTimeout(() => {
      const container = findingRefs.current[index];
      
      if (currentFinding.finding_type === 'freetext' || currentFinding.finding_type === 'freetext_unit') {
        // Textfeld fokussieren
        const textField = container.querySelector('input[type="text"], textarea');
        if (textField) textField.focus();
              } else if (currentFinding.finding_type === 'dropdown') {
        // Dropdown automatisch öffnen beim Navigieren
        const dropdownKey = `dropdown_${currentFinding.id || index}`;
        setDropdownStates(prev => ({
          ...prev,
          [dropdownKey]: true
        }));
        
        // Container fokussieren
        container.focus();
      } else {
        // Fallback: Container fokussieren
        container.focus();
      }
    }, 100);
  };

  const selectFindingOption = (optionIndex) => {
    const allFindings = getAllFindings();
    const currentFinding = allFindings[focusedFindingIndex];
    
    if (!currentFinding) return;

    const findingKey = `finding_${currentFinding.id || focusedFindingIndex}`;

    // Je nach Finding-Typ entsprechende Aktion ausführen
    switch (currentFinding.finding_type) {
      case 'dropdown':
        if (optionIndex >= 1 && optionIndex <= currentFinding.options.length) {
          const selectedOption = currentFinding.options[optionIndex - 1];
          setPreviewValues(prev => ({
            ...prev,
            [findingKey]: selectedOption
          }));
          
          // Dropdown schließen
          const dropdownKey = `dropdown_${currentFinding.id || focusedFindingIndex}`;
          setDropdownStates(prev => ({
            ...prev,
            [dropdownKey]: false
          }));
          
          // Automatisch zum nächsten Finding
          setTimeout(() => navigateToNextFinding(), 200);
        }
        break;
      case 'checkboxes':
        if (optionIndex >= 1 && optionIndex <= currentFinding.options.length) {
          const option = currentFinding.options[optionIndex - 1];
          setPreviewValues(prev => {
            const currentValues = prev[findingKey] || [];
            const isChecked = currentValues.includes(option);
            
            return {
              ...prev,
              [findingKey]: isChecked 
                ? currentValues.filter(v => v !== option)
                : [...currentValues, option]
            };
          });
          // Automatisch zum nächsten Finding nach Checkbox-Toggle
          setTimeout(() => navigateToNextFinding(), 200);
        }
        break;
      case 'yesno_comment':
        if (optionIndex === 1) {
          setPreviewValues(prev => ({
            ...prev,
            [findingKey]: 'yes'
          }));
          // Fokus auf Kommentarfeld setzen statt weitersprungen
          setTimeout(() => {
            const container = findingRefs.current[focusedFindingIndex];
            if (container) {
              const commentField = container.querySelector('textarea, input[type="text"]:last-of-type');
              if (commentField) {
                commentField.focus();
              }
            }
          }, 100);
        } else if (optionIndex === 2) {
          setPreviewValues(prev => ({
            ...prev,
            [findingKey]: 'no'
          }));
          // Fokus auf Kommentarfeld setzen statt weitersprungen
          setTimeout(() => {
            const container = findingRefs.current[focusedFindingIndex];
            if (container) {
              const commentField = container.querySelector('textarea, input[type="text"]:last-of-type');
              if (commentField) {
                commentField.focus();
              }
            }
          }, 100);
        }
        break;
    }
  };

  const getAllFindings = () => {
    const allFindings = [];
    
    // Helper function um Findings in der gleichen Reihenfolge wie Preview zu sammeln
    const addFindingsFromGroup = (findings) => {
      const grouped = groupFindingsByName(findings);
      
      Object.entries(grouped).forEach(([findingName, findingsWithSameName]) => {
        if (findingsWithSameName.length === 1 && !findingsWithSameName[0].subtitle) {
          // Einzelnes Finding ohne Subtitle
          allFindings.push(findingsWithSameName[0]);
        } else {
          // Multiple Findings mit demselben Namen - alle einzeln hinzufügen
          findingsWithSameName.forEach(finding => {
            allFindings.push(finding);
          });
        }
      });
    };
    
    // Durchlaufe allItems in chronologischer Reihenfolge
    allItems.forEach(item => {
      if (item.item_type === 'group' && item.findings && item.findings.length > 0) {
        // Findings aus Gruppen hinzufügen
        addFindingsFromGroup(item.findings);
      } else if (item.item_type === 'finding') {
        // Solitäre Findings hinzufügen
        allFindings.push(item);
      }
    });
    
    return allFindings;
  };

  // Utility function to format units (convert ^2 to superscript)
  const formatUnit = (unit) => {
    if (!unit) return '';
    return unit.replace(/\^2/g, '²').replace(/\^3/g, '³');
  };

  // Editor schließen und Test-Werte zurücksetzen
  const closeEditor = () => {
    setPreviewValues({});
    setDropdownStates({});
    setFocusedFindingIndex(0);
    setEditorOpen(false);
  };

  // Preview schließen und allItems zurücksetzen (nur wenn nicht im Editor)
  const closePreview = () => {
    if (!editorOpen) {
      setAllItems([]); // Zurücksetzen nur wenn nicht im Editor
    }
    setPreviewOpen(false);
  };

  // Template Management Functions
  const handleNewTemplate = () => {
    setCurrentTemplate(null);
    setTemplateForm({
      name: '',
      template_type: 'S',
      standard_for: { type: 'all', values: [] },
      fields_json: [],
      structured_json: {
        groups: [],
        findings: []
      },
      is_active: true // Neue Templates sind standardmäßig aktiv
    });
    setAllItems([]); // Leere einheitliche Liste
    setStandardFor('all');
    setSelectedValues([]);
    // Test-Werte zurücksetzen
    setPreviewValues({});
    setDropdownStates({});
    setFocusedFindingIndex(0);
    setEditorOpen(true);
  };

  const handleEditTemplate = (template) => {
    setCurrentTemplate(template);
    
    // Für strukturierte Templates: Lade einheitliche chronologische Liste
    let allItemsList = [];
    let fields_json = [];
    let structured_json = { groups: [], findings: [] };
    
    if (template.fields_json && Array.isArray(template.fields_json)) {
      template.fields_json.forEach(item => {
        if (item.item_type === 'field') {
          // Freifeld
          const { item_type, ...field } = item;
          allItemsList.push({ ...field, item_type: 'field' });
          fields_json.push(field); // Für Rückwärtskompatibilität
        } else if (item.item_type === 'group') {
          // Gruppe
          const { item_type, ...group } = item;
          allItemsList.push({ ...group, item_type: 'group' });
          structured_json.groups.push(group); // Für Rückwärtskompatibilität
        } else if (item.item_type === 'finding') {
          // Solitäres Finding
          const { item_type, ...finding } = item;
          allItemsList.push({ ...finding, item_type: 'finding' });
          structured_json.findings.push(finding); // Für Rückwärtskompatibilität
        } else {
          // Legacy: Elemente ohne item_type sind Freifelder
          allItemsList.push({ ...item, item_type: 'field' });
          fields_json.push(item);
        }
      });
    }
    
    // Fallback für alte Templates mit separater structured_json
    if (template.structured_json && (!template.fields_json || !Array.isArray(template.fields_json) || !template.fields_json.some(item => item.item_type))) {
      let old_structured_json = template.structured_json;
      if (typeof old_structured_json === 'string') {
        try {
          old_structured_json = JSON.parse(old_structured_json);
        } catch (e) {
          old_structured_json = { groups: [], findings: [] };
        }
      }
      if (old_structured_json && (old_structured_json.groups || old_structured_json.findings)) {
        structured_json = old_structured_json;
        
        // Legacy-Migration: Erst Felder, dann Findings, dann Gruppen
        if (template.fields_json) {
          template.fields_json.forEach(field => {
            allItemsList.push({ ...field, item_type: 'field' });
            fields_json.push(field);
          });
        }
        if (old_structured_json.findings) {
          old_structured_json.findings.forEach(finding => {
            allItemsList.push({ ...finding, item_type: 'finding' });
          });
        }
        if (old_structured_json.groups) {
          old_structured_json.groups.forEach(group => {
            allItemsList.push({ ...group, item_type: 'group' });
          });
        }
      }
    }

    setAllItems(allItemsList); // Setze die einheitliche chronologische Liste
    setTemplateForm({
      name: template.name,
      template_type: template.template_type,
      standard_for: template.standard_for || { type: 'all', values: [] },
      fields_json: fields_json, // Für Rückwärtskompatibilität
      structured_json: structured_json, // Für Rückwärtskompatibilität
      is_active: template.is_active ?? true // Lade is_active Status (default true wenn undefined)
    });

    const standardForData = template.standard_for || { type: 'all', values: [] };
    setStandardFor(standardForData.type);
    setSelectedValues(standardForData.values || []);
    // Test-Werte zurücksetzen - NUR zum Testen, nicht speichern!
    setPreviewValues({});
    setDropdownStates({});
    setFocusedFindingIndex(0);
    setEditorOpen(true);
  };

  const handleSaveTemplate = async () => {
    // Client-side Validierung - bleibt im Dialog
    if (!templateForm.name.trim()) {
      setEditorError(t('admin.reportingManagement.structuredReporting.errors.nameRequired'));
      return;
    }
    setEditorError('');

    // Get current user ID
    let currentUserId = 1; // Fallback
    try {
      console.log('Fetching current user from /auth/me...');
      const userResponse = await apiFetch('/auth/me');
      console.log('User response status:', userResponse.status);
      if (userResponse.ok) {
        const userData = await userResponse.json();
        console.log('User data received:', userData);
        currentUserId = userData.id || userData.user_id || 1;
        console.log('Set currentUserId to:', currentUserId);
      } else {
        console.warn('User response not ok, status:', userResponse.status);
      }
    } catch (error) {
      console.warn('Could not fetch current user, using fallback ID:', error);
    }

    // Für strukturierte Templates: Verwende die einheitliche chronologische Liste
    const unifiedFieldsJson = allItems.map(item => ({
      ...item,
      item_type: item.item_type // Behalte den item_type bei
    }));

    const templateData = {
      name: templateForm.name,
      template_type: templateForm.template_type,
      standard_for: {
        type: standardFor,
        values: selectedValues
      },
      fields_json: unifiedFieldsJson, // Einheitliche Struktur für strukturierte Templates
      created_by: currentUserId,
      is_active: templateForm.is_active ?? true // Sende is_active Status (default true)
    };

    console.log('=== TEMPLATE SAVE DEBUG (Structured) ===');
    console.log('Current User ID:', currentUserId);
    console.log('templateForm.is_active:', templateForm.is_active);
    console.log('allItems:', allItems);
    console.log('Unified fields_json:', unifiedFieldsJson);
    console.log('templateData as JSON:', JSON.stringify(templateData, null, 2));
    
    // Snippet-Check
    const fieldsWithSnippets = unifiedFieldsJson.filter(item => item.snippet);
    console.log(`Found ${fieldsWithSnippets.length} fields with snippets:`);
    fieldsWithSnippets.forEach(field => {
      console.log(`  - ${field.label}: ${field.snippet}`);
    });
    console.log('=========================');

    try {
      const url = currentTemplate 
        ? `${getApiBaseUrl()}/templates/${currentTemplate.id}`
        : `${getApiBaseUrl()}/templates`;
      
      const method = currentTemplate ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(templateData)
      });

      if (response.ok) {
        // Erfolg: Dialog schließen, dann Snackbar auf Hauptseite zeigen
        closeEditor();
        showSuccess(currentTemplate 
          ? t('admin.reportingManagement.structuredReporting.success.templateUpdated')
          : t('admin.reportingManagement.structuredReporting.success.templateCreated')
        );
        fetchTemplates();
      } else {
        // Backend-Fehler: Im Dialog anzeigen UND Snackbar
        const errorData = await response.json();
        const errorMsg = errorData.detail || t('admin.reportingManagement.structuredReporting.errors.saveFailed');
        setEditorError(errorMsg);
        console.error('Backend error saving template:', errorData);
        showError(errorMsg, 'ERR-STRUCTURED-003');
      }
    } catch (err) {
      console.error('Network error saving template:', err);
      showError(t('admin.reportingManagement.structuredReporting.errors.saveFailed'), 'ERR-STRUCTURED-004');
    }
  };

  // Selection Management
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(templates.map(template => template.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (templateId) => {
    setSelected(prev => 
      prev.includes(templateId) 
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId]
    );
  };

  const handleDeleteTemplate = (templateId) => {
    setItemToDelete(templateId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteSelected = () => {
    if (selected.length === 0) return;
    setItemToDelete(selected);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    const idsToDelete = Array.isArray(itemToDelete) ? itemToDelete : [itemToDelete];
    
    try {
      const deletePromises = idsToDelete.map(id =>
        fetch(`${getApiBaseUrl()}/templates/${id}`, {
          method: 'DELETE',
          headers: {
            ...getAuthHeaders()
          }
        })
      );

      const responses = await Promise.all(deletePromises);
      const allSuccessful = responses.every(response => response.ok);

      if (allSuccessful) {
        showSuccess(
          idsToDelete.length === 1
            ? t('admin.reportingManagement.structuredReporting.success.templateDeleted')
            : t('admin.reportingManagement.structuredReporting.success.templatesDeleted')
        );
        setSelected([]);
        fetchTemplates();
      } else {
        console.error('Some templates failed to delete');
        showError(t('admin.reportingManagement.structuredReporting.errors.deleteFailed'), 'ERR-STRUCTURED-005');
      }
    } catch (err) {
      console.error('Network error deleting templates:', err);
      showError(t('admin.reportingManagement.structuredReporting.errors.deleteFailed'), 'ERR-STRUCTURED-006');
    }

    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleToggleTemplateStatus = async (templateId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      const response = await apiFetch(`/templates/${templateId}`, {
        method: 'PUT',
        body: JSON.stringify({
          is_active: newStatus
        })
      });

      if (response.ok) {
        showSuccess(
          newStatus
            ? t('admin.reportingManagement.structuredReporting.success.templateActivated')
            : t('admin.reportingManagement.structuredReporting.success.templateDeactivated')
        );
        fetchTemplates();
      } else {
        console.error('Failed to toggle template status');
        showError(t('admin.reportingManagement.structuredReporting.errors.toggleStatusFailed'), 'ERR-STRUCTURED-007');
      }
    } catch (err) {
      console.error('Network error toggling template status:', err);
      showError(t('admin.reportingManagement.structuredReporting.errors.toggleStatusFailed'), 'ERR-STRUCTURED-008');
    }
  };

  const handleCopyTemplates = async () => {
    if (selected.length === 0) {
      showError(t('admin.reportingManagement.structuredReporting.errors.noTemplatesSelected'), 'ERR-STRUCTURED-009');
      return;
    }

    try {
      // Get current user ID
      let currentUserId = 1; // Fallback
      try {
        const userResponse = await apiFetch('/auth/me');
        if (userResponse.ok) {
          const userData = await userResponse.json();
          currentUserId = userData.id || userData.user_id || 1;
        }
      } catch (error) {
        console.warn('Could not fetch current user, using fallback ID');
      }

      const templatesToCopy = templates.filter(t => selected.includes(t.id));
      let successCount = 0;

      for (const template of templatesToCopy) {
        const copyData = {
          ...template,
          name: `${template.name} - Kopie`,
          is_active: true, // Kopie ist standardmäßig aktiv
          created_by: currentUserId // Aktueller Benutzer als Ersteller
        };
        delete copyData.id; // ID entfernen, damit Backend neue ID generiert
        delete copyData.created_by_name; // Entfernen, damit Backend es neu setzt

        const response = await apiFetch('/templates', {
          method: 'POST',
          body: JSON.stringify(copyData)
        });

        if (response.ok) {
          successCount++;
        } else {
          console.error('Failed to copy template:', template.name);
        }
      }

      if (successCount > 0) {
        if (successCount === 1) {
          showSuccess(t('admin.reportingManagement.structuredReporting.success.templateCopied', { name: templatesToCopy[0].name }));
        } else {
          showSuccess(t('admin.reportingManagement.structuredReporting.success.templatesCopied', { count: successCount }));
        }
        setSelected([]);
        fetchTemplates();
      }
    } catch (error) {
      console.error('Error copying templates:', error);
      showError(t('common.error'), 'ERR-STRUCTURED-010');
    }
  };

  // Normal Field Management (Freetext/Database fields)
  const handleAddField = () => {
    setCurrentField(null);
    setCurrentFieldIndex(-1);
    setFieldForm({
      id: '',
      label: '',
      type: 'freetext',
      database_field: '',
      width: 12,
      height: 3,
      snippet: '' // Snippet bei neuem Feld leer
    });
    setFieldEditorOpen(true);
  };

  const handleEditField = (field, index) => {
    setCurrentField(field);
    setCurrentFieldIndex(index);
    setFieldForm({
      id: field.id || '',
      label: field.label || '',
      type: field.type || 'freetext',
      database_field: field.database_field || '',
      width: field.width || 12,
      height: field.height || 3,
      snippet: field.snippet || '' // Snippet laden beim Bearbeiten
    });
    setFieldEditorOpen(true);
  };

  const handleSaveField = () => {
    if (!fieldForm.label.trim()) {
      showWarning(t('admin.reportingManagement.structuredReporting.errors.labelRequired'));
      return;
    }

    console.log('=== SAVE FIELD DEBUG (Structured) ===');
    console.log('fieldForm.snippet:', fieldForm.snippet);
    console.log('fieldForm:', fieldForm);

    const newField = {
      ...fieldForm,
      id: fieldForm.id || `field_${Date.now()}`,
      item_type: 'field'
    };

    // Nur Snippet hinzufügen, wenn es nicht leer ist
    if (fieldForm.snippet && fieldForm.snippet.trim()) {
      newField.snippet = fieldForm.snippet.trim();
      console.log('✅ SNIPPET ADDED TO newField:', newField.snippet);
    } else {
      console.log('❌ NO SNIPPET - fieldForm.snippet was empty or undefined');
    }

    console.log('newField:', newField);
    console.log('newField.snippet:', newField.snippet);
    console.log('newField as JSON:', JSON.stringify(newField, null, 2));

    if (currentFieldIndex >= 0) {
      // Edit existing field - finde in allItems
      const updatedItems = [...allItems];
      const globalIndex = updatedItems.findIndex(item => item.item_type === 'field' && item.id === currentField.id);
      if (globalIndex >= 0) {
        updatedItems[globalIndex] = newField;
        setAllItems(updatedItems);
        
        // Update templateForm für Rückwärtskompatibilität
        const updatedFields = [...templateForm.fields_json];
        updatedFields[currentFieldIndex] = { ...newField };
        delete updatedFields[currentFieldIndex].item_type;
        setTemplateForm(prev => ({
          ...prev,
          fields_json: updatedFields
        }));
      }
    } else {
      // Add new field - füge am Ende hinzu
      setAllItems(prev => [...prev, newField]);
      
      // Update templateForm für Rückwärtskompatibilität
      const { item_type, ...fieldWithoutType } = newField;
      setTemplateForm(prev => ({
        ...prev,
        fields_json: [...prev.fields_json, fieldWithoutType]
      }));
    }

    setFieldEditorOpen(false);
  };

  const handleDeleteField = (index) => {
    // Finde das zu löschende Feld
    const fieldToDelete = templateForm.fields_json[index];
    
    // Sicherheitsprüfung
    if (!fieldToDelete) {
      console.warn('Field to delete not found at index:', index);
      return;
    }
    
    // Entferne aus allItems
    setAllItems(prev => prev.filter(item => !(item.item_type === 'field' && item.id === fieldToDelete.id)));
    
    // Update templateForm für Rückwärtskompatibilität
    setTemplateForm(prev => ({
      ...prev,
      fields_json: prev.fields_json.filter((_, i) => i !== index)
    }));
  };

  // Field Size Update
  const updateFieldSize = (fieldIndex, newWidth, newHeight) => {
    const updatedFields = [...templateForm.fields_json];
    if (updatedFields[fieldIndex]) {
      const fieldId = updatedFields[fieldIndex].id;
      const newSize = {
        width: Math.max(1, Math.min(12, newWidth)),
        height: Math.max(1, Math.min(10, newHeight))
      };
      
      // Update templateForm.fields_json
      updatedFields[fieldIndex] = {
        ...updatedFields[fieldIndex],
        ...newSize
      };
      setTemplateForm(prev => ({ ...prev, fields_json: updatedFields }));
      
      // Update allItems für Synchronisation
      setAllItems(prev => prev.map(item => {
        if (item.item_type === 'field' && item.id === fieldId) {
          return { ...item, ...newSize };
        }
        return item;
      }));
    }
  };

  // Move Field (Drag & Drop)
  const moveItem = (fromIndex, toIndex) => {
    const updatedItems = [...allItems];
    const [movedItem] = updatedItems.splice(fromIndex, 1);
    updatedItems.splice(toIndex, 0, movedItem);
    setAllItems(updatedItems);
    
    // Update templateForm für Rückwärtskompatibilität
    updateLegacyTemplateForm();
  };

  // Drag & Drop Handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDropIndex(index);
    }
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      moveItem(draggedIndex, dropIndex);
    }
    setDraggedIndex(null);
    setDropIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDropIndex(null);
  };

  // Group-internal Drag & Drop Handlers
  const moveGroupFinding = (groupId, fromIndex, toIndex) => {
    setAllItems(prev => {
      const updatedItems = prev.map(item => {
        if (item.item_type === 'group' && item.id === groupId) {
          const updatedFindings = [...item.findings];
          const [movedFinding] = updatedFindings.splice(fromIndex, 1);
          updatedFindings.splice(toIndex, 0, movedFinding);
          return { ...item, findings: updatedFindings };
        }
        return item;
      });
      return updatedItems;
    });
    
    // Update templateForm für Rückwärtskompatibilität
    setTimeout(() => updateLegacyTemplateForm(), 0);
  };

  const handleGroupDragStart = (e, findingIndex, groupId) => {
    setGroupDraggedIndex(findingIndex);
    setActiveGroupId(groupId);
    e.dataTransfer.effectAllowed = 'move';
    e.stopPropagation(); // Verhindert, dass das Gruppen-Drag startet
  };

  const handleGroupDragOver = (e, findingIndex, groupId) => {
    e.preventDefault();
    e.stopPropagation();
    if (groupDraggedIndex !== null && groupDraggedIndex !== findingIndex && activeGroupId === groupId) {
      setGroupDropIndex(findingIndex);
    }
  };

  const handleGroupDrop = (e, dropIndex, groupId) => {
    e.preventDefault();
    e.stopPropagation();
    if (groupDraggedIndex !== null && groupDraggedIndex !== dropIndex && activeGroupId === groupId) {
      moveGroupFinding(groupId, groupDraggedIndex, dropIndex);
    }
    setGroupDraggedIndex(null);
    setGroupDropIndex(null);
    setActiveGroupId(null);
  };

  const handleGroupDragEnd = () => {
    setGroupDraggedIndex(null);
    setGroupDropIndex(null);
    setActiveGroupId(null);
  };

  // Resize Handlers - EXAKT aus FreetextReporting.js kopiert
  const handleResizeStart = (e, fieldId, direction) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Finde den korrekten Index in templateForm.fields_json basierend auf field.id
    const fieldIndex = templateForm.fields_json.findIndex(f => f.id === fieldId);
    const field = templateForm.fields_json[fieldIndex];
    if (!field || fieldIndex === -1) return;
    
    setIsResizing(true);
    
    resizeData.current = {
      fieldIndex,
      direction,
      startPos: { x: e.clientX, y: e.clientY },
      startSize: { width: field.width || 12, height: field.height || 3 }
    };

    document.addEventListener('mousemove', handleResizeMove, { passive: false });
    document.addEventListener('mouseup', handleResizeEnd, { passive: false });
    document.body.style.cursor = direction === 'width' ? 'ew-resize' : 'ns-resize';
    document.body.style.userSelect = 'none';
  };

  const handleResizeMove = (e) => {
    e.preventDefault();
    
    if (resizeData.current.fieldIndex === -1) return;
    
    const { fieldIndex, direction, startPos, startSize } = resizeData.current;
    const deltaX = e.clientX - startPos.x;
    const deltaY = e.clientY - startPos.y;
    
    if (direction === 'width') {
      const widthChange = Math.round(deltaX / 50);
      const newWidth = Math.max(1, Math.min(12, startSize.width + widthChange));
      
      if (newWidth !== templateForm.fields_json[fieldIndex]?.width) {
        updateFieldSize(fieldIndex, newWidth, startSize.height);
      }
    } else if (direction === 'height') {
      const heightChange = Math.round(deltaY / 25);
      const newHeight = Math.max(1, Math.min(10, startSize.height + heightChange));
      
      if (newHeight !== templateForm.fields_json[fieldIndex]?.height) {
        updateFieldSize(fieldIndex, startSize.width, newHeight);
      }
    }
  };

  const handleResizeEnd = () => {
    setIsResizing(false);
    resizeData.current = {
      fieldIndex: -1,
      direction: null,
      startPos: { x: 0, y: 0 },
      startSize: { width: 0, height: 0 }
    };

    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };

  // Structured Finding Management
  const handleAddStructuredItem = (type, groupId = null) => {
    setStructuredEditType(type);
    setCurrentStructuredItem(null);
    setCurrentStructuredIndex(-1);
    
    // Refs zurücksetzen
    optionInputRefs.current = [];
    setFocusOptionIndex(-1);
    
    if (type === 'group') {
      setStructuredForm({
        type: 'group',
        name: '',
        subtitle: '',
        finding_type: 'freetext',
        placeholder: '',
        unit: '',
        custom_unit: '',
        options: [],
        comment_placeholder: '',
        database_field: '',
        group_id: null
      });
    } else {
      setStructuredForm({
        type: 'finding',
        name: '',
        subtitle: '',
        finding_type: 'freetext',
        placeholder: '',
        unit: '',
        custom_unit: '',
        options: [],
        comment_placeholder: '',
        database_field: '',
        group_id: groupId || selectedGroup || null
      });
    }
    
    // selectedGroup setzen für die UI-Anzeige
    if (groupId) {
      setSelectedGroup(groupId);
    }
    
    setStructuredEditorOpen(true);
  };

  const handleEditStructuredItem = (item, index, type) => {
    setStructuredEditType(type);
    setCurrentStructuredItem(item);
    setCurrentStructuredIndex(index);
    
    // Refs zurücksetzen
    optionInputRefs.current = [];
    setFocusOptionIndex(-1);
    
    setStructuredForm({
      type: type,
      name: item.name || '',
      subtitle: item.subtitle || '',
      finding_type: item.finding_type || 'freetext',
      placeholder: item.placeholder || '',
      unit: item.unit || '',
      custom_unit: item.custom_unit || '',
      options: item.options || [],
      comment_placeholder: item.comment_placeholder || '',
      group_id: item.group_id || null
    });
    
    setStructuredEditorOpen(true);
  };

  const handleSaveStructuredItem = () => {
    if (!structuredForm.name.trim()) {
      showWarning(t('admin.reportingManagement.structuredReporting.errors.nameRequired'));
      return;
    }

    const newItem = {
      ...structuredForm,
      id: currentStructuredItem?.id || `${structuredForm.type}_${Date.now()}`,
      item_type: structuredForm.type === 'group' ? 'group' : 'finding'
    };

    if (currentStructuredIndex >= 0) {
      // Edit existing item - finde in allItems
      const updatedItems = [...allItems];
      
      if (structuredForm.type === 'group') {
        // Group bearbeiten - Top-Level
        const globalIndex = updatedItems.findIndex(item => 
          item.id === currentStructuredItem.id && item.item_type === 'group'
        );
        if (globalIndex >= 0) {
          // Behalte bestehende findings bei
          updatedItems[globalIndex] = {
            ...newItem,
            findings: currentStructuredItem?.findings || []
          };
          setAllItems(updatedItems);
        }
      } else {
        // Finding bearbeiten - könnte in Group oder Top-Level sein
        if (currentStructuredItem.group_id || structuredForm.group_id) {
          // 🔥 Finding ist in einer Group - aktualisiere es dort
          const groupId = currentStructuredItem.group_id || structuredForm.group_id;
          const groupIndex = updatedItems.findIndex(item => 
            item.item_type === 'group' && item.id === groupId
          );
          
          if (groupIndex >= 0) {
            // Finde das Finding innerhalb der Group
            const findingIndex = updatedItems[groupIndex].findings.findIndex(f => 
              f.id === currentStructuredItem.id
            );
            
            if (findingIndex >= 0) {
              // Aktualisiere das Finding in der Group
              updatedItems[groupIndex] = {
                ...updatedItems[groupIndex],
                findings: [
                  ...updatedItems[groupIndex].findings.slice(0, findingIndex),
                  newItem,
                  ...updatedItems[groupIndex].findings.slice(findingIndex + 1)
                ]
              };
              setAllItems(updatedItems);
              console.log('✅ Finding in Group aktualisiert:', newItem);
            }
          }
        } else {
          // Finding ist Top-Level (ohne Group)
          const globalIndex = updatedItems.findIndex(item => 
            item.id === currentStructuredItem.id && item.item_type === 'finding'
          );
          if (globalIndex >= 0) {
            updatedItems[globalIndex] = newItem;
            setAllItems(updatedItems);
            console.log('✅ Top-Level Finding aktualisiert:', newItem);
          }
        }
      }
    } else {
      // Add new item
      if (structuredForm.type === 'group') {
        // Neue Gruppe hinzufügen
        setAllItems(prev => [...prev, { ...newItem, findings: [] }]);
      } else {
        // Neues Finding hinzufügen
        if (structuredForm.group_id) {
          // Finding zu bestehender Gruppe hinzufügen
          // 🔥 Stelle sicher dass group_id im Finding gespeichert ist
          const findingWithGroupId = {
            ...newItem,
            group_id: structuredForm.group_id
          };
          
          setAllItems(prev => prev.map(item => {
            if (item.item_type === 'group' && item.id === structuredForm.group_id) {
              return {
                ...item,
                findings: [...(item.findings || []), findingWithGroupId]
              };
            }
            return item;
          }));
          console.log('✅ Neues Finding zu Group hinzugefügt:', findingWithGroupId);
        } else {
          // Solitäres Finding hinzufügen
          setAllItems(prev => [...prev, newItem]);
          console.log('✅ Neues solitäres Finding hinzugefügt:', newItem);
        }
      }
    }

    // Update templateForm für Rückwärtskompatibilität
    updateLegacyTemplateForm();

    setStructuredEditorOpen(false);
    
    // Reset selectedGroup nach dem Speichern
    setSelectedGroup(null);
  };

  const handleDeleteStructuredItem = (index, type, groupId = null) => {
    if (type === 'group') {
      // Finde das zu löschende Element
      const groupToDelete = templateForm.structured_json.groups[index];
      setAllItems(prev => prev.filter(item => !(item.item_type === 'group' && item.id === groupToDelete.id)));
    } else {
      if (groupId) {
        // Finding aus einer Gruppe löschen - TODO: Implement group findings deletion
        const group = templateForm.structured_json.groups.find(g => g.id === groupId);
        if (group && group.findings[index]) {
          const findingToDelete = group.findings[index];
          // Entferne Finding aus der Gruppe in allItems
          setAllItems(prev => prev.map(item => {
            if (item.item_type === 'group' && item.id === groupId) {
              return {
                ...item,
                findings: item.findings.filter((_, i) => i !== index)
              };
            }
            return item;
          }));
        }
      } else {
        // Finding ohne Gruppe löschen
        const findingToDelete = templateForm.structured_json.findings[index];
        setAllItems(prev => prev.filter(item => !(item.item_type === 'finding' && item.id === findingToDelete.id)));
      }
    }
    
    // Update templateForm für Rückwärtskompatibilität
    updateLegacyTemplateForm();
  };

  // Toggle Group Expansion
  const toggleGroupExpansion = (groupId) => {
    const newExpandedGroups = new Set(expandedGroups);
    if (newExpandedGroups.has(groupId)) {
      newExpandedGroups.delete(groupId);
    } else {
      newExpandedGroups.add(groupId);
    }
    setExpandedGroups(newExpandedGroups);
  };

  // Helper function to update legacy templateForm structure
  const updateLegacyTemplateForm = () => {
    const fields_json = [];
    const structured_json = { groups: [], findings: [] };
    
    allItems.forEach(item => {
      if (item.item_type === 'field') {
        const { item_type, ...field } = item;
        fields_json.push(field);
      } else if (item.item_type === 'group') {
        const { item_type, ...group } = item;
        structured_json.groups.push(group);
      } else if (item.item_type === 'finding') {
        const { item_type, ...finding } = item;
        structured_json.findings.push(finding);
      }
    });
    
    setTemplateForm(prev => ({
      ...prev,
      fields_json,
      structured_json
    }));
  };

  // CSV Export/Import Functions
  const handleExportTemplate = (template) => {
    try {
      const csvData = exportTemplateToCSV(template);
      downloadCSVGuideWithData(csvData, template.name);
      showSuccess(t('admin.reportingManagement.structuredReporting.exportSuccess'));
    } catch (error) {
      console.error('Error exporting template:', error);
      showError(t('admin.reportingManagement.structuredReporting.exportError', { error: error.message }), 'ERR-STRUCTURED-007');
    }
  };

  const handleExportSelected = () => {
    if (selected.length === 0) return;
    
    try {
      // Alle ausgewählten Templates sammeln
      const selectedTemplates = selected.map(templateId => 
        templates.find(t => t.id === templateId)
      ).filter(template => template);
      
      if (selectedTemplates.length === 0) return;
      
      // Eine einzige CSV für alle Templates erstellen
      const csvData = exportMultipleTemplatesToCSV(selectedTemplates);
      
      // Dateiname mit Template-Namen oder Anzahl
      const fileName = selectedTemplates.length === 1 
        ? selectedTemplates[0].name
        : t('admin.reportingManagement.structuredReporting.exportFilenameMultiple', { count: selectedTemplates.length });
      
      downloadCSVGuideWithData(csvData, fileName);
      showSuccess(t('admin.reportingManagement.structuredReporting.exportMultipleSuccess', { count: selectedTemplates.length }));
    } catch (error) {
      console.error('Error exporting templates:', error);
      showError(t('admin.reportingManagement.structuredReporting.exportError', { error: error.message }), 'ERR-STRUCTURED-008');
    }
  };

  const handleImportCSV = () => {
    setImportDialogOpen(true);
    setImportFile(null);
    setImportPreview(null);
    setImportErrors([]);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImportFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvContent = e.target.result;
        const template = parseCSVToTemplate(csvContent);
        const errors = validateTemplate(template);
        
        setImportPreview(template);
        setImportErrors(errors);
      } catch (error) {
        setImportErrors([error.message]);
        setImportPreview(null);
      }
    };
    reader.readAsText(file);
  };

  const handleConfirmImport = async () => {
    if (!importPreview || importErrors.length > 0) return;

    try {
      // Get current user ID
      let currentUserId = 1;
      try {
        const userResponse = await apiFetch('/auth/me');
        if (userResponse.ok) {
          const userData = await userResponse.json();
          currentUserId = userData.id || userData.user_id || 1;
        }
      } catch (error) {
        console.warn('Could not fetch current user, using fallback ID');
      }

      const templateData = {
        ...importPreview,
        created_by: currentUserId
      };

      const response = await fetch(`${getApiBaseUrl()}/templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(templateData)
      });

      if (response.ok) {
        setImportDialogOpen(false);
        showSuccess(t('admin.reportingManagement.structuredReporting.importSuccess'));
        fetchTemplates();
      } else {
        const errorData = await response.json();
        console.error('Backend error importing template:', errorData);
        showError(errorData.detail || t('admin.reportingManagement.structuredReporting.importError'), 'ERR-STRUCTURED-009');
      }
    } catch (error) {
      console.error('Network error importing template:', error);
      showError(t('admin.reportingManagement.structuredReporting.importErrorDetail', { error: error.message }), 'ERR-STRUCTURED-010');
    }
  };

  const handleGenerateGuide = async () => {
    try {
      const guidePdf = await generateCSVGuide();
      guidePdf.save(t('admin.reportingManagement.structuredReporting.guideFilename'));
      showSuccess(t('admin.reportingManagement.structuredReporting.guideDownloaded'));
    } catch (error) {
      console.error('Error generating guide:', error);
      showError(t('admin.reportingManagement.structuredReporting.guideError', { error: error.message }), 'ERR-STRUCTURED-011');
    }
  };

  // HTML Import Handlers
  const handleHtmlImport = () => {
    setHtmlImportDialogOpen(true);
    setHtmlImportFiles([]);
    setHtmlImportResults(null);
  };

  const handleHtmlFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    setHtmlImportFiles(files);
    setHtmlImportResults(null);
  };

  const handleConfirmHtmlImport = async () => {
    if (htmlImportFiles.length === 0) return;

    setHtmlImportLoading(true);
    try {
      let currentUserId = 1;
      try {
        const meResponse = await apiFetch('/auth/me');
        if (meResponse.ok) {
          const meData = await meResponse.json();
          currentUserId = meData.id;
        }
      } catch (e) { /* fallback to 1 */ }

      const formData = new FormData();
      htmlImportFiles.forEach(file => {
        formData.append('files', file);
      });
      formData.append('created_by', currentUserId.toString());

      const response = await apiFetch('/templates/import-html', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setHtmlImportResults(data);

        if (data.successful > 0) {
          if (data.failed > 0) {
            showSuccess(t('admin.reportingManagement.structuredReporting.htmlImportPartialSuccess', {
              successful: data.successful,
              total: data.total,
              failed: data.failed
            }));
          } else {
            showSuccess(t('admin.reportingManagement.structuredReporting.htmlImportSuccess', { count: data.successful }));
          }
          fetchTemplates();
        } else {
          showError(t('admin.reportingManagement.structuredReporting.htmlImportError'), 'ERR-HTML-IMPORT-001');
        }
      } else {
        const errorData = await response.json();
        showError(errorData.detail || t('admin.reportingManagement.structuredReporting.htmlImportError'), 'ERR-HTML-IMPORT-002');
      }
    } catch (error) {
      console.error('Error importing HTML templates:', error);
      showError(t('admin.reportingManagement.structuredReporting.htmlImportErrorDetail', { error: error.message }), 'ERR-HTML-IMPORT-003');
    } finally {
      setHtmlImportLoading(false);
    }
  };

  // Render Functions
  const renderTemplateList = () => {
    const filteredTemplates = templates.filter(template => 
      !search || template.name.toLowerCase().includes(search.toLowerCase())
    );
    
    const hasSelected = selected.length > 0;
    const exactlyOneSelected = selected.length === 1;

    return (
      <>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StructuredIcon color="primary" />
            {t('admin.reportingManagement.structuredReporting.title')}
          </Typography>
          <Button
            className="button"
            startIcon={<AddIcon />}
            onClick={handleNewTemplate}
          >
            {t('admin.reportingManagement.structuredReporting.newTemplate')}
          </Button>
        </Box>

        {/* Aktionsleiste */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="subtitle2" sx={{ mr: 2 }}>
                {hasSelected
                  ? t('admin.reportingManagement.structuredReporting.selectedTemplates', { count: selected.length })
                  : t('admin.reportingManagement.structuredReporting.noTemplates')
                }
              </Typography>
              <Divider orientation="vertical" flexItem />
              <Tooltip title={t('common.edit')}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const template = templates.find(t => t.id === selected[0]);
                      handleEditTemplate(template);
                    }}
                    disabled={!exactlyOneSelected || loading}
                  >
                    <EditIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('admin.reportingManagement.structuredReporting.copyTemplateTooltip')}>
                <span>
                  <IconButton
                    size="small"
                    onClick={handleCopyTemplates}
                    disabled={!hasSelected || loading}
                  >
                    <CopyIcon />
                  </IconButton>
                </span>
              </Tooltip>
              {/* DELETE BUTTON VERSTECKT - Templates werden soft-deleted (aktiv/inaktiv) */}
              <Divider orientation="vertical" flexItem />
              <Tooltip title={t('admin.reportingManagement.structuredReporting.csvExportTooltip')}>
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={handleExportSelected}
                    disabled={!hasSelected || loading}
                  >
                    <ExportIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('admin.reportingManagement.structuredReporting.previewTitle')}>
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => {
                      const template = templates.find(t => t.id === selected[0]);
                      setCurrentTemplate(template);
                      
                      // Template für Vorschau laden - gleiche Logik wie handleEditTemplate
                      // Für strukturierte Templates: Aufteilen der einheitlichen fields_json
                      let fields_json = [];
                      let structured_json = { groups: [], findings: [] };
                      
                      if (template.fields_json && Array.isArray(template.fields_json)) {
                        template.fields_json.forEach(item => {
                          if (item.item_type === 'field') {
                            // Freifeld - entferne item_type und füge zu fields_json hinzu
                            const { item_type, ...field } = item;
                            fields_json.push(field);
                          } else if (item.item_type === 'group') {
                            // Gruppe - entferne item_type und füge zu groups hinzu
                            const { item_type, ...group } = item;
                            structured_json.groups.push(group);
                          } else if (item.item_type === 'finding') {
                            // Solitäres Finding - entferne item_type und füge zu findings hinzu
                            const { item_type, ...finding } = item;
                            structured_json.findings.push(finding);
                          } else {
                            // Legacy: Elemente ohne item_type sind Freifelder
                            fields_json.push(item);
                          }
                        });
                      }
                      
                      // Fallback für alte Templates mit separater structured_json
                      if (template.structured_json && (!template.fields_json || !Array.isArray(template.fields_json) || !template.fields_json.some(item => item.item_type))) {
                        let old_structured_json = template.structured_json;
                        if (typeof old_structured_json === 'string') {
                          try {
                            old_structured_json = JSON.parse(old_structured_json);
                          } catch (e) {
                            old_structured_json = { groups: [], findings: [] };
                          }
                        }
                        if (old_structured_json && (old_structured_json.groups || old_structured_json.findings)) {
                          structured_json = old_structured_json;
                        }
                        
                        // Alte fields_json für Freifelder
                        if (template.fields_json) {
                          fields_json = template.fields_json;
                        }
                      }

                      // allItems für Vorschau aufbauen - gleiche Logik wie in handleEditTemplate
                      let previewAllItems = [];
                      
                      if (template.fields_json && Array.isArray(template.fields_json)) {
                        template.fields_json.forEach(item => {
                          if (item.item_type === 'field') {
                            // Freifeld
                            const { item_type, ...field } = item;
                            previewAllItems.push({ ...field, item_type: 'field' });
                          } else if (item.item_type === 'group') {
                            // Gruppe
                            const { item_type, ...group } = item;
                            previewAllItems.push({ ...group, item_type: 'group' });
                          } else if (item.item_type === 'finding') {
                            // Solitäres Finding
                            const { item_type, ...finding } = item;
                            previewAllItems.push({ ...finding, item_type: 'finding' });
                          } else {
                            // Legacy: Elemente ohne item_type sind Freifelder
                            previewAllItems.push({ ...item, item_type: 'field' });
                          }
                        });
                      }
                      
                      // Fallback für alte Templates mit separater structured_json
                      if (template.structured_json && (!template.fields_json || !Array.isArray(template.fields_json) || !template.fields_json.some(item => item.item_type))) {
                        let old_structured_json = template.structured_json;
                        if (typeof old_structured_json === 'string') {
                          try {
                            old_structured_json = JSON.parse(old_structured_json);
                          } catch (e) {
                            old_structured_json = { groups: [], findings: [] };
                          }
                        }
                        if (old_structured_json && (old_structured_json.groups || old_structured_json.findings)) {
                          // Legacy-Migration: Erst Felder, dann Findings, dann Gruppen
                          if (template.fields_json) {
                            template.fields_json.forEach(field => {
                              previewAllItems.push({ ...field, item_type: 'field' });
                            });
                          }
                          if (old_structured_json.findings) {
                            old_structured_json.findings.forEach(finding => {
                              previewAllItems.push({ ...finding, item_type: 'finding' });
                            });
                          }
                          if (old_structured_json.groups) {
                            old_structured_json.groups.forEach(group => {
                              previewAllItems.push({ ...group, item_type: 'group' });
                            });
                          }
                        }
                      }

                      // Setze sowohl allItems als auch templateForm für Rückwärtskompatibilität
                      setAllItems(previewAllItems);
                      setTemplateForm({
                        name: template.name,
                        template_type: template.template_type,
                        standard_for: template.standard_for || { type: 'all', values: [] },
                        fields_json: fields_json,
                        structured_json: structured_json
                      });
                      
                      setPreviewOpen(true);
                    }}
                    disabled={!exactlyOneSelected || loading}
                  >
                    <PreviewIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            
            {/* Import-Aktionen und Such-Bereich */}
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Button
                startIcon={<ImportIcon />}
                onClick={handleHtmlImport}
                size="small"
                className="button"
              >
                {t('admin.reportingManagement.structuredReporting.htmlImport')}
              </Button>
              <Button
                startIcon={<ImportIcon />}
                onClick={handleImportCSV}
                size="small"
                className="button"
              >
                {t('admin.reportingManagement.structuredReporting.csvImport')}
              </Button>
              <Button
                startIcon={<PdfIcon />}
                onClick={handleGenerateGuide}
                size="small"
                className="button"
              >
                {t('admin.reportingManagement.structuredReporting.csvGuide')}
              </Button>
              <Divider orientation="vertical" flexItem />
              <TextField
                label={t('common.search')}
                value={search}
                onChange={e => setSearch(e.target.value)}
                size="small"
                sx={{ minWidth: 220, height: '40px', '& .MuiInputBase-root': { height: '40px' } }}
              />
              <Button
                variant={showInactive ? "contained" : "outlined"}
                startIcon={showInactive ? <VisibilityIcon /> : <VisibilityOffIcon />}
                onClick={() => setShowInactive(!showInactive)}
                color={showInactive ? undefined : "primary"}
                sx={showInactive 
                  ? { backgroundColor: 'var(--rd-danger)', color: 'var(--rd-primary-contrast)', '&:hover': { backgroundColor: 'var(--rd-error)' }, height: '40px' } 
                  : { height: '40px' }
                }
              >
                {showInactive ? t('admin.reportingManagement.structuredReporting.hideInactive') : t('admin.reportingManagement.structuredReporting.showInactive')}
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Tabelle */}
        <Paper sx={{ width: '100%' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 420px)' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>
                    <Checkbox
                      indeterminate={selected.length > 0 && selected.length < filteredTemplates.length}
                      checked={filteredTemplates.length > 0 && selected.length === filteredTemplates.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>
                    {t('admin.reportingManagement.structuredReporting.table.name')}
                  </TableCell>
                  <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>
                    {t('admin.reportingManagement.structuredReporting.table.standardFor')}
                  </TableCell>
                  <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>
                    {t('admin.reportingManagement.structuredReporting.table.fields')}
                  </TableCell>
                  <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2, width: '40%' }}>
                    {t('admin.reportingManagement.structuredReporting.table.findings')}
                  </TableCell>
                  <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>
                    {t('admin.reportingManagement.structuredReporting.table.createdBy')}
                  </TableCell>
                  <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow sx={{ height: 64 }}>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : filteredTemplates.length === 0 ? (
                  <TableRow sx={{ height: 64 }}>
                    <TableCell colSpan={7} align="center">
                      {search ? 'Keine Templates gefunden' : t('admin.reportingManagement.structuredReporting.noTemplates')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTemplates.map((template) => {
                    const isSelected = selected.includes(template.id);
                    const standardForData = template.standard_for || { type: 'all', values: [] };
                    
                    // Prüfe ob Template neue einheitliche Struktur verwendet
                    const hasUnifiedStructure = template.fields_json && 
                      Array.isArray(template.fields_json) && 
                      template.fields_json.some(item => item.item_type);
                    
                    let fieldsCount = 0;
                    let totalFindings = 0;
                    let groupsCount = 0;
                    
                    if (hasUnifiedStructure) {
                      // Neue einheitliche Struktur - zähle basierend auf item_type
                      template.fields_json.forEach(item => {
                        if (item.item_type === 'field') {
                          fieldsCount++;
                        } else if (item.item_type === 'group') {
                          groupsCount++;
                          if (item.findings && item.findings.length > 0) {
                            totalFindings += item.findings.length;
                          }
                        } else if (item.item_type === 'finding') {
                          totalFindings++;
                        }
                      });
                    } else {
                      // Alte Struktur für Backwards Compatibility
                      fieldsCount = template.fields_json ? template.fields_json.length : 0;
                      
                      let structured_json = template.structured_json;
                      if (typeof structured_json === 'string') {
                        try {
                          structured_json = JSON.parse(structured_json);
                        } catch (e) {
                          structured_json = { groups: [], findings: [] };
                        }
                      }
                      if (!structured_json) {
                        structured_json = { groups: [], findings: [] };
                      }
                      
                      const findingsCount = structured_json.findings?.length || 0;
                      groupsCount = structured_json.groups?.length || 0;
                      const groupFindingsCount = structured_json.groups?.reduce((acc, group) => acc + (group.findings?.length || 0), 0) || 0;
                      totalFindings = findingsCount + groupFindingsCount;
                    }
                    
                    return (
                      <TableRow key={template.id} selected={isSelected} sx={{ height: 64 }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleSelect(template.id)}
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {template.name}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {standardForData.type === 'all' ? (
                            t('admin.reportingManagement.structuredReporting.standardFor.all')
                          ) : standardForData.type === 'modalities' ? (
                            t('admin.reportingManagement.structuredReporting.standardFor.modalities') + ` (${standardForData.values?.length || 0})`
                          ) : (
                            t('admin.reportingManagement.structuredReporting.standardFor.examinations') + ` (${standardForData.values?.length || 0})`
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {fieldsCount} {fieldsCount === 1 ? t('admin.reportingManagement.structuredReporting.table.freetextField') : t('admin.reportingManagement.structuredReporting.table.freetextFields')}
                        </TableCell>
                        <TableCell sx={{ width: '40%' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="body2">
                              {totalFindings} {t('admin.reportingManagement.structuredReporting.table.structuredFindings')}
                            </Typography>
                            {groupsCount > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                {groupsCount} {t('admin.reportingManagement.structuredReporting.table.groups')}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {template.created_by_name || t('common.unknown')}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Chip
                            label={template.is_active !== false ? 'Aktiv' : 'Inaktiv'}
                            size="small"
                            sx={template.is_active !== false
                              ? { backgroundColor: 'var(--rd-success)', color: 'var(--rd-primary-contrast)' }
                              : { backgroundColor: 'var(--rd-danger)', color: 'var(--rd-primary-contrast)' }
                            }
                            onClick={() => {
                              handleToggleTemplateStatus(template.id, template.is_active !== false);
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </>
    );
  };

  const renderTemplateEditor = () => (
    <Dialog 
      open={editorOpen} 
      onClose={closeEditor}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle>
        {currentTemplate 
          ? t('admin.reportingManagement.structuredReporting.editTemplate')
          : t('admin.reportingManagement.structuredReporting.newTemplate')
        }
      </DialogTitle>
      <DialogContent>
        {editorError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEditorError('')}>
            {editorError}
          </Alert>
        )}
        <Box sx={{ mt: 2, display: 'flex', gap: 3 }}>
          {/* Linke Seite - Konfiguration */}
          <Box sx={{ flex: 1, pr: 2, borderRight: (theme) => `1px solid ${theme.palette.divider}` }}>
            {/* Template Name */}
            <TextField
              label={t('admin.reportingManagement.structuredReporting.templateName')}
              value={templateForm.name}
              onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}

              fullWidth
              required
              sx={{ mb: 2 }}
            />
            
            {/* Active/Inactive Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={templateForm.is_active ?? true}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, is_active: e.target.checked }))}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: 'var(--rd-accent)',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: 'var(--rd-accent)',
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">
                    {(templateForm.is_active ?? true) ? 'Template ist aktiv' : 'Template ist inaktiv'}
                  </Typography>
                  <Chip
                    label={(templateForm.is_active ?? true) ? 'Aktiv' : 'Inaktiv'}
                    size="small"
                    sx={(templateForm.is_active ?? true)
                      ? { backgroundColor: 'var(--rd-success)', color: 'var(--rd-primary-contrast)' }
                      : { backgroundColor: 'var(--rd-danger)', color: 'var(--rd-primary-contrast)' }
                    }
                  />
                </Box>
              }
              sx={{ mb: 3 }}
            />

            {/* Standard für */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                {t('admin.reportingManagement.structuredReporting.standardFor.title')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={standardFor === 'all'}
                      onChange={() => {
                        setStandardFor('all');
                        setSelectedValues([]);
                        setModalityFilter('');
                      }}
                    />
                  }
                  label={t('admin.reportingManagement.structuredReporting.standardFor.all')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={standardFor === 'modalities'}
                      onChange={() => {
                        setStandardFor('modalities');
                        setSelectedValues([]);
                        setModalityFilter('');
                      }}
                    />
                  }
                  label={t('admin.reportingManagement.structuredReporting.standardFor.modalities')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={standardFor === 'examinations'}
                      onChange={() => {
                        setStandardFor('examinations');
                        setSelectedValues([]);
                        setModalityFilter('');
                      }}
                    />
                  }
                  label={t('admin.reportingManagement.structuredReporting.standardFor.examinations')}
                />
              </Box>

              {/* Modalität/Untersuchung Auswahl */}
              {standardFor !== 'all' && (
                <Box>
                  {/* Modalitäts-Filter für Untersuchungen */}
                  {standardFor === 'examinations' && (
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Nach Modalität filtern</InputLabel>
                      <Select
                        value={modalityFilter}
                        label="Nach Modalität filtern"
                        onChange={(e) => setModalityFilter(e.target.value)}
                      >
                        <MenuItem value="">Alle Modalitäten</MenuItem>
                        {ALL_MODALITIES.map((modality) => (
                          <MenuItem key={modality.id} value={modality.code}>
                            {modality.code} - {modality.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {/* Checkbox-Liste */}
                  <Paper variant="outlined" sx={{ p: 2, maxHeight: '300px', overflowY: 'auto' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {standardFor === 'modalities' 
                        ? `${selectedValues.length} von ${ALL_MODALITIES.length} ausgewählt`
                        : `${selectedValues.length} von ${examinations.filter(e => !modalityFilter || e.modality === modalityFilter).length} ausgewählt`
                      }
                    </Typography>
                    <Divider sx={{ mb: 1 }} />
                    {standardFor === 'modalities' ? (
                      // Modalitäten-Liste
                      ALL_MODALITIES.map((modality) => (
                        <FormControlLabel
                          key={modality.id}
                          control={
                            <Checkbox
                              checked={selectedValues.includes(modality.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedValues([...selectedValues, modality.id]);
                                } else {
                                  setSelectedValues(selectedValues.filter(id => id !== modality.id));
                                }
                              }}
                            />
                          }
                          label={`${modality.code} - ${modality.name}`}
                          sx={{ display: 'block', mb: 0.5 }}
                        />
                      ))
                    ) : (
                      // Untersuchungen-Liste (gefiltert)
                      examinations
                        .filter(exam => !modalityFilter || exam.modality === modalityFilter)
                        .map((exam) => (
                          <FormControlLabel
                            key={exam.id}
                            control={
                              <Checkbox
                                checked={selectedValues.includes(exam.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedValues([...selectedValues, exam.id]);
                                  } else {
                                    setSelectedValues(selectedValues.filter(id => id !== exam.id));
                                  }
                                }}
                              />
                            }
                            label={`${exam.short_code} - ${exam.name}`}
                            sx={{ display: 'block', mb: 0.5 }}
                          />
                        ))
                    )}
                  </Paper>
                </Box>
              )}
            </Box>

            {/* Feld hinzufügen Section - EXAKT wie FreetextReporting */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">{t('admin.reportingManagement.freetextReporting.templateEditor.addFieldSection')}</Typography>
                <Button
                  className="button"
                  startIcon={<AddIcon />}
                  onClick={handleAddField}
                >
                  {t('common.add')}
                </Button>
              </Box>
            </Box>

            {/* Strukturierte Felder Section */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">{t('admin.reportingManagement.structuredReporting.structuredFields')}</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => handleAddStructuredItem('group')}
                    className="button"
                    size="small"
                  >
                    {t('admin.reportingManagement.structuredReporting.addGroup')}
                  </Button>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => handleAddStructuredItem('finding')}
                    className="button"
                    size="small"
                  >
                    {t('admin.reportingManagement.structuredReporting.addFinding')}
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Einheitliche chronologische Feldliste */}
            <Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: '350px', overflowY: 'auto' }}>
                {allItems.map((item, index) => {
                  if (item.item_type === 'field') {
                    // Render Freifeld
                    const fieldIndex = templateForm.fields_json.findIndex(f => f.id === item.id);
                    return (
                      <Box key={item.id || index}>
                        {/* Orange Drop-Indikator Linie */}
                        {dropIndex === index && draggedIndex !== null && draggedIndex !== index && (
                          <Box
                            sx={{
                              height: '3px',
                              backgroundColor: 'var(--rd-accent)',
                              margin: '4px 0',
                              borderRadius: '2px',
                              boxShadow: '0 0 4px color-mix(in srgb, var(--rd-accent) 60%, transparent)'
                            }}
                          />
                        )}
                        
                        <Card 
                          variant="outlined" 
                          sx={{ 
                            p: 1,
                            cursor: 'grab',
                            boxShadow: draggedIndex === index ? 3 : 1,
                            opacity: draggedIndex === index ? 0.5 : 1,
                            border: dropIndex === index && draggedIndex !== null && draggedIndex !== index ? '2px solid var(--rd-accent)' : 'none',
                            '&:active': { cursor: 'grabbing' }
                          }}
                          draggable
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDrop={(e) => handleDrop(e, index)}
                          onDragEnd={handleDragEnd}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DragIcon sx={{ color: 'grey.400', cursor: 'grab' }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle2">
                                {item.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {item.database_field ? `${item.database_field}` : t('admin.reportingManagement.freetextReporting.fieldEditor.freetextField')} • {item.width}/12 × {item.height}
                              </Typography>
                            </Box>
                            <IconButton onClick={() => handleEditField(item, fieldIndex)} size="small">
                              <EditIcon />
                            </IconButton>
                            <IconButton onClick={() => handleDeleteField(fieldIndex)} size="small" color="error">
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Card>
                      </Box>
                    );
                  } else if (item.item_type === 'finding') {
                    // Render solitäres Finding
                    const findingIndex = templateForm.structured_json.findings.findIndex(f => f.id === item.id);
                    return (
                      <Box key={item.id || index}>
                        {/* Orange Drop-Indikator Linie */}
                        {dropIndex === index && draggedIndex !== null && draggedIndex !== index && (
                          <Box
                            sx={{
                              height: '3px',
                              backgroundColor: 'var(--rd-accent)',
                              margin: '4px 0',
                              borderRadius: '2px',
                              boxShadow: '0 0 4px color-mix(in srgb, var(--rd-accent) 60%, transparent)'
                            }}
                          />
                        )}
                        
                        <Card 
                          variant="outlined" 
                          sx={{ 
                            p: 1,
                            cursor: 'grab',
                            boxShadow: draggedIndex === index ? 3 : 1,
                            opacity: draggedIndex === index ? 0.5 : 1,
                            border: dropIndex === index && draggedIndex !== null && draggedIndex !== index ? '2px solid var(--rd-accent)' : 'none',
                            '&:active': { cursor: 'grabbing' }
                          }}
                          draggable
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDrop={(e) => handleDrop(e, index)}
                          onDragEnd={handleDragEnd}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DragIcon sx={{ color: 'grey.400', cursor: 'grab' }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2">
                                {item.name}
                                {item.subtitle && <span style={{ color: 'var(--rd-text-secondary)' }}> - {item.subtitle}</span>}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {t(`admin.reportingManagement.structuredReporting.fieldEditor.types.${item.finding_type}`)}
                              </Typography>
                            </Box>
                            <IconButton onClick={() => handleEditStructuredItem(item, findingIndex, 'finding')} size="small">
                              <EditIcon />
                            </IconButton>
                            <IconButton onClick={() => handleDeleteStructuredItem(findingIndex, 'finding')} size="small" color="error">
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Card>
                      </Box>
                    );
                  } else if (item.item_type === 'group') {
                    // Render Gruppe
                    const groupIndex = templateForm.structured_json.groups.findIndex(g => g.id === item.id);
                    const isExpanded = expandedGroups.has(item.id);
                    return (
                      <Box key={item.id || index}>
                        {/* Orange Drop-Indikator Linie */}
                        {dropIndex === index && draggedIndex !== null && draggedIndex !== index && (
                          <Box
                            sx={{
                              height: '3px',
                              backgroundColor: 'var(--rd-accent)',
                              margin: '4px 0',
                              borderRadius: '2px',
                              boxShadow: '0 0 4px color-mix(in srgb, var(--rd-accent) 60%, transparent)'
                            }}
                          />
                        )}
                        
                        <Card 
                          variant="outlined" 
                          sx={{ 
                            p: 1, 
                            backgroundColor: (theme) => theme.palette.action.hover,
                            cursor: 'grab',
                            boxShadow: draggedIndex === index ? 3 : 1,
                            opacity: draggedIndex === index ? 0.5 : 1,
                            border: dropIndex === index && draggedIndex !== null && draggedIndex !== index ? '2px solid var(--rd-accent)' : 'none',
                            '&:active': { cursor: 'grabbing' }
                          }}
                          draggable
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDrop={(e) => handleDrop(e, index)}
                          onDragEnd={handleDragEnd}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DragIcon sx={{ color: 'grey.400', cursor: 'grab' }} />
                            <IconButton
                              size="small"
                              onClick={() => toggleGroupExpansion(item.id)}
                              sx={{ p: 0.5 }}
                            >
                              {isExpanded ? (
                                <FolderOpenIcon sx={{ color: 'grey.600' }} />
                              ) : (
                                <FolderIcon sx={{ color: 'grey.600' }} />
                              )}
                            </IconButton>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {item.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {item.findings?.length || 0} {t('admin.reportingManagement.structuredReporting.findingsInGroup')}
                              </Typography>
                            </Box>
                            <Button
                              size="small"
                              onClick={() => {
                                handleAddStructuredItem('finding', item.id);
                              }}
                              startIcon={<AddIcon />}
                              className="button"
                            >
                              {t('admin.reportingManagement.structuredReporting.addToGroup')}
                            </Button>
                            <IconButton onClick={() => handleEditStructuredItem(item, groupIndex, 'group')} size="small">
                              <EditIcon />
                            </IconButton>
                            <IconButton onClick={() => handleDeleteStructuredItem(groupIndex, 'group')} size="small" color="error">
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Card>
                        
                        {/* Findings in Gruppe - nur anzeigen wenn ausgeklappt */}
                        {isExpanded && item.findings?.map((finding, findingIndex) => (
                          <Box key={finding.id || findingIndex} sx={{ ml: 3, mt: 0.5 }}>
                            {/* Orange Drop-Indikator Linie für Gruppen-Findings */}
                            {groupDropIndex === findingIndex && 
                             groupDraggedIndex !== null && 
                             groupDraggedIndex !== findingIndex && 
                             activeGroupId === item.id && (
                              <Box
                                sx={{
                                  height: '3px',
                                  backgroundColor: 'var(--rd-accent)',
                                  margin: '4px 0',
                                  borderRadius: '2px',
                                  boxShadow: '0 0 4px color-mix(in srgb, var(--rd-accent) 60%, transparent)'
                                }}
                              />
                            )}
                            
                            <Card 
                              variant="outlined" 
                              sx={{ 
                                p: 1,
                                cursor: 'grab',
                                boxShadow: (groupDraggedIndex === findingIndex && activeGroupId === item.id) ? 3 : 1,
                                opacity: (groupDraggedIndex === findingIndex && activeGroupId === item.id) ? 0.5 : 1,
                                border: (groupDropIndex === findingIndex && 
                                        groupDraggedIndex !== null && 
                                        groupDraggedIndex !== findingIndex && 
                                        activeGroupId === item.id) ? '2px solid var(--rd-accent)' : 'none',
                                '&:active': { cursor: 'grabbing' }
                              }}
                              draggable
                              onDragStart={(e) => handleGroupDragStart(e, findingIndex, item.id)}
                              onDragOver={(e) => handleGroupDragOver(e, findingIndex, item.id)}
                              onDrop={(e) => handleGroupDrop(e, findingIndex, item.id)}
                              onDragEnd={handleGroupDragEnd}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DragIcon sx={{ color: 'grey.400', cursor: 'grab' }} />
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2">
                                    {finding.name}
                                    {finding.subtitle && <span style={{ color: 'var(--rd-text-secondary)' }}> - {finding.subtitle}</span>}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {t(`admin.reportingManagement.structuredReporting.fieldEditor.types.${finding.finding_type}`)}
                                  </Typography>
                                </Box>
                                <IconButton onClick={() => handleEditStructuredItem(finding, findingIndex, 'finding', item.id)} size="small">
                                  <EditIcon />
                                </IconButton>
                                <IconButton onClick={() => handleDeleteStructuredItem(findingIndex, 'finding', item.id)} size="small" color="error">
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </Card>
                          </Box>
                        ))}
                      </Box>
                    );
                  }
                  return null;
                })}
              </Box>
            </Box>
          </Box>

          {/* Rechte Seite - Vorschau */}
          <Box sx={{ flex: 1, pl: 2 }}>
            <Box sx={{ 
              border: '2px solid var(--rd-accent)', 
              borderRadius: 1, 
              p: 3, 
              height: '75vh',
              minHeight: '600px',
              backgroundColor: (theme) => theme.palette.background.default,
              overflow: 'auto'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">
                  {t('admin.reportingManagement.structuredReporting.previewTitle')}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={numpadMode}
                      onChange={(e) => setNumpadMode(e.target.checked)}
                    />
                  }
                  label={t('admin.reportingManagement.structuredReporting.numpadMode')}
                />
              </Box>
              {renderPreview()}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEditorOpen(false)} className="button-cancel">
          {t('common.cancel')}
        </Button>
        <Button 
          onClick={handleSaveTemplate}
          className="button"
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Field Editor Dialog (für normale Felder) - Identisch zu FreetextReporting
  const renderFieldEditor = () => (
    <Dialog open={fieldEditorOpen} onClose={() => setFieldEditorOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        {currentField ? t('admin.reportingManagement.freetextReporting.fieldEditor.editField') : t('admin.reportingManagement.freetextReporting.fieldEditor.addField')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Bezeichnung */}
          <TextField
            label={t('admin.reportingManagement.freetextReporting.fieldEditor.label')}
            value={fieldForm.label}
            onChange={(e) => setFieldForm({ ...fieldForm, label: e.target.value })}
            fullWidth
            required
            sx={{ mb: 3 }}
            helperText={t('admin.reportingManagement.freetextReporting.fieldEditor.labelHelperText')}
          />

          {/* Feldtyp */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('admin.reportingManagement.freetextReporting.fieldEditor.type')}</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={fieldForm.type === 'freetext'}
                    onChange={() => setFieldForm({ ...fieldForm, type: 'freetext', database_field: '' })}
                  />
                }
                label={t('admin.reportingManagement.freetextReporting.fieldEditor.freetextField')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={fieldForm.type === 'database'}
                    onChange={() => setFieldForm({ ...fieldForm, type: 'database' })}
                  />
                }
                label={t('admin.reportingManagement.freetextReporting.fieldEditor.databaseField')}
              />
            </Box>
          </Box>

          {/* Datenbankverknüpfung */}
          {fieldForm.type === 'database' && (
            <Autocomplete
              options={DATABASE_FIELDS}
              getOptionLabel={(option) => option.label}
              value={DATABASE_FIELDS.find(f => f.key === fieldForm.database_field) || DATABASE_FIELDS[0]}
              onChange={(event, newValue) => {
                setFieldForm({ ...fieldForm, database_field: newValue?.key || '' });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('admin.reportingManagement.freetextReporting.fieldEditor.databaseLink')}
                  sx={{ mb: 3 }}
                />
              )}
            />
          )}

          {/* Snippet einfügen - nur für Freitextfelder */}
          {fieldForm.type === 'freetext' && (
            <Autocomplete
              freeSolo
              options={availableSnippets}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                // Verwende label mit @ Präfix, falls vorhanden
                if (option.label && option.label.trim()) {
                  return option.label.startsWith('@') ? option.label : `@${option.label}`;
                }
                // Fallback auf Name falls label leer
                return option.name || option.id || '';
              }}
              filterOptions={(options, { inputValue }) => {
                const searchLower = inputValue.toLowerCase();
                return options.filter(option => {
                  const label = (option.label || '').toLowerCase();
                  const name = (option.name || '').toLowerCase();
                  const content = (option.content || '').toLowerCase();
                  
                  // Suche in Label, Name und Content
                  return label.includes(searchLower) || 
                         name.includes(searchLower) || 
                         content.includes(searchLower);
                });
              }}
              value={
                // Finde das passende Snippet-Objekt oder verwende den String-Wert
                fieldForm.snippet 
                  ? availableSnippets.find(s => 
                      `@${s.label}` === fieldForm.snippet || 
                      s.label === fieldForm.snippet
                    ) || fieldForm.snippet
                  : ''
              }
              onChange={(event, newValue) => {
                if (typeof newValue === 'string') {
                  setFieldForm({ ...fieldForm, snippet: newValue });
                } else if (newValue && newValue.label) {
                  // Füge @ hinzu falls nicht vorhanden
                  const snippetValue = newValue.label.startsWith('@') 
                    ? newValue.label 
                    : `@${newValue.label}`;
                  setFieldForm({ ...fieldForm, snippet: snippetValue });
                } else {
                  setFieldForm({ ...fieldForm, snippet: '' });
                }
              }}
              inputValue={fieldForm.snippet || ''}
              onInputChange={(event, newInputValue, reason) => {
                // Nur bei manueller Eingabe (nicht bei "reset")
                if (reason === 'input') {
                  setFieldForm({ ...fieldForm, snippet: newInputValue });
                }
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id} sx={{ display: 'block !important', py: 1 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5, width: '100%' }}>
                    {option.name || option.label}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ 
                      display: 'block',
                      whiteSpace: 'normal',
                      lineHeight: 1.3,
                      width: '100%'
                    }}
                  >
                    {option.content?.substring(0, 100)}{option.content?.length > 100 ? '...' : ''}
                  </Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Snippet einfügen (optional)"
                  placeholder="Suche nach @Label, Name oder Inhalt..."
                  helperText="Snippet wird später im Report Editor automatisch ersetzt"
                />
              )}
              loading={snippetsLoading}
              sx={{ mb: 3 }}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setFieldEditorOpen(false)} className="button-cancel">
          {t('common.cancel')}
        </Button>
        <Button onClick={handleSaveField} className="button">
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Render Resizable Field (für Freitextfelder in der Vorschau) - EXAKT aus FreetextReporting.js kopiert
  const renderResizableField = (field, index, isPurePreview = false) => {
    return (
      <Box
        className="resize-field"
        key={field.id}
        sx={{
          position: 'relative',
          backgroundColor: 'transparent',
          width: `${(field.width || 12) * 8.333}%`,
          marginBottom: 2,
          ...(!isPurePreview && {
            '&:hover': {
              '& .action-buttons': { opacity: 1 },
              '& .drag-handle': { opacity: 0.7 }
            }
          })
        }}
      >
        {/* Titel über dem Feld */}
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
          {field.label}
        </Typography>

        {/* Hauptfeld mit Handles */}
        <Box sx={{ position: 'relative' }}>
          <TextField
            multiline
            rows={field.height || 3}
            fullWidth
            variant="outlined"
            disabled
            placeholder={field.database_field ? t('admin.reportingManagement.freetextReporting.fieldEditor.autoFilled', { field: field.database_field }) : t('admin.reportingManagement.freetextReporting.fieldEditor.freetextPlaceholder')}
            sx={{ 
              '& .MuiInputBase-root': { 
                backgroundColor: field.database_field 
                  ? (theme) => theme.palette.mode === 'dark' ? 'color-mix(in srgb, var(--rd-accent) 15%, transparent)' : 'color-mix(in srgb, var(--rd-primary) 12%, transparent)'
                  : (theme) => theme.palette.background.paper
              }
            }}
          />
          
          {/* Rechts-Handle für Breite - nur im Editor-Preview */}
          {!isPurePreview && (
            <Box
              className="drag-handle"
              onMouseDown={(e) => handleResizeStart(e, field.id, 'width')}
              sx={{
                position: 'absolute',
                top: '50%',
                right: -2,
                width: 4,
                height: 40,
                backgroundColor: 'var(--rd-info)',
                cursor: 'ew-resize',
                borderRadius: '2px',
                transform: 'translateY(-50%)',
                opacity: 0,
                transition: 'opacity 0.2s',
                zIndex: 10,
                '&:hover': { 
                  opacity: 1
                }
              }}
            />
          )}
          
          {/* Unten-Handle für Höhe - nur im Editor-Preview */}
          {!isPurePreview && (
            <Box
              className="drag-handle"
              onMouseDown={(e) => handleResizeStart(e, field.id, 'height')}
              sx={{
                position: 'absolute',
                bottom: -2,
                left: '50%',
                width: 40,
                height: 4,
                backgroundColor: 'var(--rd-success)',
                cursor: 'ns-resize',
                borderRadius: '2px',
                transform: 'translateX(-50%)',
                opacity: 0,
                transition: 'opacity 0.2s',
                zIndex: 10,
                '&:hover': { 
                  opacity: 1
                }
              }}
            />
          )}
        </Box>
        
        {/* Datenbankfeld-Info */}
        {field.database_field && (
          <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
            🔗 {DATABASE_FIELDS.find(f => f.key === field.database_field)?.label || field.database_field}
          </Typography>
        )}
        
        {/* Größe-Info */}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          {field.height} {t('admin.reportingManagement.freetextReporting.fieldEditor.rows')}
        </Typography>
        
        {/* Action-Buttons - nur im Editor-Preview */}
        {!isPurePreview && (
          <Box 
            className="action-buttons"
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              display: 'flex',
              gap: 0.5,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 1,
              p: 0.5,
              opacity: 0,
              transition: 'opacity 0.2s'
            }}
          >
            {/* Feld bearbeiten */}
            <IconButton
              size="small"
              onClick={() => {
                const fieldIndex = templateForm.fields_json.findIndex(f => f.id === field.id);
                handleEditField(field, fieldIndex);
              }}
              sx={{ width: 24, height: 24 }}
            >
              <EditIcon sx={{ fontSize: 14 }} />
            </IconButton>
            
            {/* Feld löschen */}
            <IconButton
              size="small"
              onClick={() => {
                const fieldIndex = templateForm.fields_json.findIndex(f => f.id === field.id);
                handleDeleteField(fieldIndex);
              }}
              color="error"
              sx={{ width: 24, height: 24 }}
            >
              <DeleteIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        )}
      </Box>
    );
  };

  // Structured Item Editor Dialog
  const renderStructuredEditor = () => (
    <Dialog 
      open={structuredEditorOpen} 
      onClose={() => {
        setStructuredEditorOpen(false);
        setSelectedGroup(null); // Reset selectedGroup beim Schließen
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {structuredEditType === 'group' 
          ? (currentStructuredItem 
              ? t('admin.reportingManagement.structuredReporting.editGroup')
              : t('admin.reportingManagement.structuredReporting.newGroup')
            )
          : (currentStructuredItem 
              ? t('admin.reportingManagement.structuredReporting.editFinding')
              : t('admin.reportingManagement.structuredReporting.newFinding')
            )
        }
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label={structuredEditType === 'group' 
            ? t('admin.reportingManagement.structuredReporting.fieldEditor.groupName')
            : t('admin.reportingManagement.structuredReporting.fieldEditor.findingTitle')
          }
          value={structuredForm.name}
          onChange={(e) => setStructuredForm(prev => ({ ...prev, name: e.target.value }))}
          margin="normal"
        />

        {structuredEditType === 'finding' && (
          <>
            <TextField
              fullWidth
              label={t('admin.reportingManagement.structuredReporting.fieldEditor.subtitle')}
              value={structuredForm.subtitle}
              onChange={(e) => setStructuredForm(prev => ({ ...prev, subtitle: e.target.value }))}
              margin="normal"
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>{t('admin.reportingManagement.structuredReporting.fieldEditor.findingType')}</InputLabel>
              <Select
                value={structuredForm.finding_type}
                onChange={(e) => {
                  const newType = e.target.value;
                  setStructuredForm(prev => ({ 
                    ...prev, 
                    finding_type: newType,
                    options: (newType === 'dropdown' || newType === 'checkboxes') ? [''] : [],
                    placeholder: '',
                    unit: '',
                    custom_unit: '',
                    comment_placeholder: '',
                    database_field: ''
                  }));
                }}
                label={t('admin.reportingManagement.structuredReporting.fieldEditor.findingType')}
              >
                {FINDING_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {t(type.label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {(structuredForm.finding_type === 'freetext' || structuredForm.finding_type === 'freetext_unit') && (
              <TextField
                fullWidth
                label={t('admin.reportingManagement.structuredReporting.fieldEditor.placeholder')}
                value={structuredForm.placeholder}
                onChange={(e) => setStructuredForm(prev => ({ ...prev, placeholder: e.target.value }))}
                margin="normal"
              />
            )}

            {structuredForm.finding_type === 'freetext_unit' && (
              <>
                <FormControl fullWidth margin="normal">
                  <InputLabel>{t('admin.reportingManagement.structuredReporting.fieldEditor.unit')}</InputLabel>
                  <Select
                    value={structuredForm.unit}
                    onChange={(e) => setStructuredForm(prev => ({ ...prev, unit: e.target.value, custom_unit: '' }))}
                    label={t('admin.reportingManagement.structuredReporting.fieldEditor.unit')}
                  >
                    {AVAILABLE_UNITS.map((unit) => (
                      <MenuItem key={unit.value} value={unit.value}>
                        {unit.value === 'custom' ? t(unit.label) : formatUnit(unit.label)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {structuredForm.unit === 'custom' && (
                  <TextField
                    fullWidth
                    label={t('admin.reportingManagement.structuredReporting.fieldEditor.customUnit')}
                    value={structuredForm.custom_unit}
                    onChange={(e) => setStructuredForm(prev => ({ ...prev, custom_unit: e.target.value }))}
                    margin="normal"
                  />
                )}
              </>
            )}

            {(structuredForm.finding_type === 'dropdown' || structuredForm.finding_type === 'checkboxes') && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('admin.reportingManagement.structuredReporting.fieldEditor.options')}
                </Typography>
                {structuredForm.options.map((option, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={option}
                      inputRef={el => optionInputRefs.current[index] = el}
                      onChange={(e) => {
                        const newOptions = [...structuredForm.options];
                        newOptions[index] = e.target.value;
                        setStructuredForm(prev => ({ ...prev, options: newOptions }));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          // Neue leere Option hinzufügen
                          const newOptions = [...structuredForm.options];
                          newOptions.splice(index + 1, 0, ''); // Nach der aktuellen Option einfügen
                          setStructuredForm(prev => ({ ...prev, options: newOptions }));
                          
                          // Fokus auf die neue Option setzen
                          setFocusOptionIndex(index + 1);
                        }
                      }}
                      placeholder={t('admin.reportingManagement.structuredReporting.fieldEditor.optionPlaceholder')}
                      autoFocus={index === 0} // Erste Option automatisch fokussieren
                    />
                    <IconButton
                      onClick={() => {
                        const newOptions = structuredForm.options.filter((_, i) => i !== index);
                        setStructuredForm(prev => ({ ...prev, options: newOptions }));
                        // Refs array anpassen
                        optionInputRefs.current = optionInputRefs.current.filter((_, i) => i !== index);
                      }}
                      size="small"
                      color="error"
                      disabled={structuredForm.options.length <= 1} // Mindestens eine Option behalten
                    >
                      <RemoveIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => setStructuredForm(prev => ({ ...prev, options: [...prev.options, ''] }))}
                  size="small"
                  className="button"
                >
                  {t('admin.reportingManagement.structuredReporting.fieldEditor.addOption')}
                </Button>
              </Box>
            )}

            {structuredForm.finding_type === 'yesno_comment' && (
              <TextField
                fullWidth
                label={t('admin.reportingManagement.structuredReporting.fieldEditor.commentPlaceholder')}
                value={structuredForm.comment_placeholder}
                onChange={(e) => setStructuredForm(prev => ({ ...prev, comment_placeholder: e.target.value }))}
                margin="normal"
              />
            )}

            {structuredForm.finding_type === 'database' && (
              <Autocomplete
                options={DATABASE_FIELDS.filter(f => f.key !== '')}
                getOptionLabel={(option) => option.label}
                value={DATABASE_FIELDS.find(f => f.key === structuredForm.database_field) || null}
                onChange={(event, newValue) => {
                  setStructuredForm(prev => ({ ...prev, database_field: newValue?.key || '' }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('admin.reportingManagement.freetextReporting.fieldEditor.databaseLink')}
                    margin="normal"
                  />
                )}
              />
            )}

            {/* Gruppe auswählen - NUR bei Bearbeitung bestehender Findings */}
            {currentStructuredItem && !selectedGroup ? (
              <FormControl fullWidth margin="normal">
                <InputLabel>{t('admin.reportingManagement.structuredReporting.fieldEditor.assignToGroup')}</InputLabel>
                <Select
                  value={structuredForm.group_id || ''}
                  onChange={(e) => setStructuredForm(prev => ({ ...prev, group_id: e.target.value || null }))}
                  label={t('admin.reportingManagement.structuredReporting.fieldEditor.assignToGroup')}
                >
                  <MenuItem value="">
                    {t('admin.reportingManagement.structuredReporting.fieldEditor.noGroup')}
                  </MenuItem>
                  {templateForm.structured_json.groups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : selectedGroup ? (
              /* Anzeige der vorgewählten Gruppe bei "Zu Gruppe hinzufügen" */
              <Box sx={{ mt: 2, p: 2, backgroundColor: (theme) => theme.palette.action.hover, borderRadius: 1 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  {t('admin.reportingManagement.structuredReporting.fieldEditor.assignedToGroup')}
                </Typography>
                <Typography variant="body2">
                  {templateForm.structured_json.groups.find(g => g.id === selectedGroup)?.name}
                </Typography>
              </Box>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => {
          setStructuredEditorOpen(false);
          setSelectedGroup(null); // Reset selectedGroup beim Abbrechen
        }} className="button-cancel">
          {t('common.cancel')}
        </Button>
        <Button 
          onClick={handleSaveStructuredItem}
          className="button"
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Preview Functions
  const renderPreviewFinding = (finding, index, globalIndex, isInSubGroup = false) => {
    const isActive = numpadMode && focusedFindingIndex === globalIndex;
    
    return (
      <Box 
        key={finding.id || index}
        sx={{ 
          mb: 1,
          border: isActive ? '2px solid var(--rd-accent)' : (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 1,
          position: 'relative',
          outline: 'none', // Entferne nativen Focus-Rahmen
          '&:focus': {
            outline: 'none' // Sicherstellen, dass auch bei Focus kein nativer Rahmen erscheint
          }
        }}
        ref={el => findingRefs.current[globalIndex] = el}
        tabIndex={numpadMode ? 0 : -1}
      >


        {/* Horizontales Layout für strukturierte Findings */}
        {finding.finding_type === 'freetext' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, maxWidth: '100%' }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.primary', minWidth: '300px', flexShrink: 0 }}>
              {isInSubGroup ? (finding.subtitle || finding.name) : finding.name}
              {!isInSubGroup && finding.subtitle && ` - ${finding.subtitle}`}:
            </Typography>
            <TextField
              placeholder={finding.placeholder || t('admin.reportingManagement.structuredReporting.preview.enterText')}
              size="small"
              sx={{ width: '400px', flexShrink: 0 }}
              value={previewValues[`finding_${finding.id || index}`] || ""}
              onChange={(e) => {
                const findingKey = `finding_${finding.id || index}`;
                setPreviewValues(prev => ({
                  ...prev,
                  [findingKey]: e.target.value
                }));
              }}
              autoFocus={numpadMode && isActive}
            />
          </Box>
        )}

        {finding.finding_type === 'freetext_unit' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, maxWidth: '100%' }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.primary', minWidth: '300px', flexShrink: 0 }}>
              {isInSubGroup ? (finding.subtitle || finding.name) : finding.name}
              {!isInSubGroup && finding.subtitle && ` - ${finding.subtitle}`}:
            </Typography>
            <TextField
              placeholder={finding.placeholder || t('admin.reportingManagement.structuredReporting.preview.enterValue')}
              size="small"
              sx={{ width: '400px', flexShrink: 0 }}
              value={previewValues[`finding_${finding.id || index}`] || ""}
              onChange={(e) => {
                const findingKey = `finding_${finding.id || index}`;
                setPreviewValues(prev => ({
                  ...prev,
                  [findingKey]: e.target.value
                }));
              }}
              autoFocus={numpadMode && isActive}
            />
            <Typography variant="body2" sx={{ minWidth: 40, flexShrink: 0 }}>
              {formatUnit(finding.unit === 'custom' ? finding.custom_unit : finding.unit)}
            </Typography>
          </Box>
        )}

        {finding.finding_type === 'dropdown' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, maxWidth: '100%' }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.primary', minWidth: '300px', flexShrink: 0 }}>
              {isInSubGroup ? (finding.subtitle || finding.name) : finding.name}
              {!isInSubGroup && finding.subtitle && ` - ${finding.subtitle}`}:
            </Typography>
            <FormControl size="small" sx={{ width: '400px', flexShrink: 0 }}>
              <Select
                value={previewValues[`finding_${finding.id || index}`] || ""}
                displayEmpty
                open={dropdownStates[`dropdown_${finding.id || index}`] || false}
                onOpen={() => {
                  const dropdownKey = `dropdown_${finding.id || index}`;
                  setDropdownStates(prev => ({
                    ...prev,
                    [dropdownKey]: true
                  }));
                }}
                onClose={() => {
                  const dropdownKey = `dropdown_${finding.id || index}`;
                  setDropdownStates(prev => ({
                    ...prev,
                    [dropdownKey]: false
                  }));
                }}
                renderValue={(value) => value || t('admin.reportingManagement.structuredReporting.preview.selectOption')}
                onChange={(e) => {
                  const findingKey = `finding_${finding.id || index}`;
                  setPreviewValues(prev => ({
                    ...prev,
                    [findingKey]: e.target.value
                  }));
                }}
              >
                {finding.options?.map((option, optIndex) => (
                  <MenuItem key={optIndex} value={option}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <span>
                        {option}
                        {numpadMode && (
                          <sub style={{ 
                            color: 'var(--rd-accent)', 
                            fontWeight: 'bold',
                            fontSize: '0.8em'
                          }}>
                            {optIndex + 1}
                          </sub>
                        )}
                      </span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {finding.finding_type === 'checkboxes' && (
          <Box sx={{ maxWidth: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.primary', minWidth: '300px', flexShrink: 0 }}>
                {isInSubGroup ? (finding.subtitle || finding.name) : finding.name}
                {!isInSubGroup && finding.subtitle && ` - ${finding.subtitle}`}:
              </Typography>
              <Box sx={{ width: '400px', flexShrink: 0 }}>
                {finding.options?.map((option, optIndex) => {
                  const findingKey = `finding_${finding.id || index}`;
                  const currentValues = previewValues[findingKey] || [];
                  const isChecked = currentValues.includes(option);
                  
                  return (
                    <FormControlLabel
                      key={optIndex}
                      control={
                        <Checkbox 
                          checked={isChecked}
                          onChange={(e) => {
                            setPreviewValues(prev => {
                              const currentValues = prev[findingKey] || [];
                              return {
                                ...prev,
                                [findingKey]: e.target.checked 
                                  ? [...currentValues, option]
                                  : currentValues.filter(v => v !== option)
                              };
                            });
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <span>
                            {option}
                            {numpadMode && (
                              <sub style={{ 
                                color: 'var(--rd-accent)', 
                                fontWeight: 'bold',
                                fontSize: '0.8em'
                              }}>
                                {optIndex + 1}
                              </sub>
                            )}
                          </span>
                        </Box>
                      }
                    />
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}

        {finding.finding_type === 'yesno_comment' && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, maxWidth: '100%' }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.primary', minWidth: '300px', flexShrink: 0 }}>
                {isInSubGroup ? (finding.subtitle || finding.name) : finding.name}
                {!isInSubGroup && finding.subtitle && ` - ${finding.subtitle}`}:
              </Typography>
              <RadioGroup 
                row 
                sx={{ width: '400px', flexShrink: 0 }}
                value={previewValues[`finding_${finding.id || index}`] || ""}
                onChange={(e) => {
                  const findingKey = `finding_${finding.id || index}`;
                  setPreviewValues(prev => ({
                    ...prev,
                    [findingKey]: e.target.value
                  }));
                }}
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <span>
                        {t('admin.reportingManagement.structuredReporting.preview.yes')}
                        {numpadMode && (
                          <sub style={{ 
                            color: 'var(--rd-accent)', 
                            fontWeight: 'bold',
                            fontSize: '0.8em'
                          }}>
                            1
                          </sub>
                        )}
                      </span>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="no"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <span>
                        {t('admin.reportingManagement.structuredReporting.preview.no')}
                        {numpadMode && (
                          <sub style={{ 
                            color: 'var(--rd-accent)', 
                            fontWeight: 'bold',
                            fontSize: '0.8em'
                          }}>
                            2
                          </sub>
                        )}
                      </span>
                    </Box>
                  }
                />
              </RadioGroup>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{ minWidth: '300px', flexShrink: 0 }}></Box>
              <TextField
                multiline
                rows={2}
                placeholder={finding.comment_placeholder || t('admin.reportingManagement.structuredReporting.preview.enterComment')}
                size="small"
                sx={{ width: '400px', flexShrink: 0 }}
                value={previewValues[`finding_${finding.id || index}_comment`] || ""}
                onChange={(e) => {
                  const commentKey = `finding_${finding.id || index}_comment`;
                  setPreviewValues(prev => ({
                    ...prev,
                    [commentKey]: e.target.value
                  }));
                }}
                onKeyDown={(e) => {
                  if (numpadMode && e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    navigateToNextFinding();
                  }
                }}
              />
            </Box>
          </Box>
        )}

        {finding.finding_type === 'database' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, maxWidth: '100%' }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.primary', minWidth: '300px', flexShrink: 0 }}>
              {isInSubGroup ? (finding.subtitle || finding.name) : finding.name}
              {!isInSubGroup && finding.subtitle && ` - ${finding.subtitle}`}:
            </Typography>
            <TextField
              size="small"
              disabled
              placeholder={t('admin.reportingManagement.freetextReporting.fieldEditor.autoFilled', { field: DATABASE_FIELDS.find(f => f.key === finding.database_field)?.label || finding.database_field })}
              sx={{ 
                width: '400px', 
                flexShrink: 0,
                '& .MuiInputBase-root': { 
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'color-mix(in srgb, var(--rd-accent) 15%, transparent)' : 'color-mix(in srgb, var(--rd-primary) 12%, transparent)'
                }
              }}
            />
          </Box>
        )}
      </Box>
    );
  };

  // Gruppiere Findings nach Namen für die Vorschau
  const groupFindingsByName = (findings) => {
    const grouped = {};
    findings.forEach((finding, index) => {
      const name = finding.name;
      if (!grouped[name]) {
        grouped[name] = [];
      }
      grouped[name].push({ ...finding, originalIndex: index });
    });
    return grouped;
  };

  const renderPreview = (isPurePreview = false) => {
    let globalIndex = 0;
    
    // Prüfen ob überhaupt Inhalte vorhanden sind
    if (!allItems || allItems.length === 0) {
      return (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          {t('admin.reportingManagement.freetextReporting.templateEditor.noFieldsAdded')}
        </Typography>
      );
    }

    // Render alle Elemente in chronologischer Reihenfolge
    const allElements = [];
    const processedFindingIndices = new Set();
    
    for (let itemIndex = 0; itemIndex < allItems.length; itemIndex++) {
      const item = allItems[itemIndex];
      
      if (item.item_type === 'field') {
        // Render Freifeld
        allElements.push(renderResizableField(item, itemIndex, isPurePreview));
        
      } else if (item.item_type === 'group') {
        // Render Gruppe mit ihren Findings
        const groupedGroupFindings = groupFindingsByName(item.findings || []);
        
        allElements.push(
          <Box key={item.id || itemIndex} sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, borderBottom: (theme) => `2px solid ${theme.palette.primary.main}`, pb: 1 }}>
              {item.name}
            </Typography>
            
            {Object.entries(groupedGroupFindings).map(([findingName, findingsWithSameName]) => {
              if (findingsWithSameName.length === 1 && !findingsWithSameName[0].subtitle) {
                return renderPreviewFinding(findingsWithSameName[0], findingsWithSameName[0].originalIndex, globalIndex++, false);
              } else {
                return (
                  <Box key={`${item.id}-${findingName}`} sx={{ mb: 2, ml: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'text.primary' }}>
                      {findingName}
                    </Typography>
                    {findingsWithSameName.map((finding) => 
                      renderPreviewFinding(finding, finding.originalIndex, globalIndex++, true)
                    )}
                  </Box>
                );
              }
            })}
          </Box>
        );
        
      } else if (item.item_type === 'finding' && !processedFindingIndices.has(itemIndex)) {
        // Render solitäres Finding - sammle alle nachfolgenden mit gleichem Namen
        const sameName = [item];
        processedFindingIndices.add(itemIndex);
        
        // Sammle alle nachfolgenden Findings mit demselben Namen
        for (let j = itemIndex + 1; j < allItems.length; j++) {
          const nextItem = allItems[j];
          if (nextItem.item_type === 'finding' && nextItem.name === item.name && !processedFindingIndices.has(j)) {
            sameName.push(nextItem);
            processedFindingIndices.add(j);
          } else if (nextItem.item_type !== 'finding') {
            break; // Stoppe bei anderem Item-Type
          }
        }
        
        if (sameName.length === 1 && !sameName[0].subtitle) {
          // Einzelnes Finding ohne Subtitle
          allElements.push(renderPreviewFinding(sameName[0], 0, globalIndex++, false));
        } else {
          // Multiple Findings mit demselben Namen oder mit Subtitle
          allElements.push(
            <Box key={`finding-${item.name}-${itemIndex}`} sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, borderBottom: (theme) => `2px solid ${theme.palette.primary.main}`, pb: 1, color: 'text.primary' }}>
                {item.name}
              </Typography>
              {sameName.map((finding, findingIndex) => 
                renderPreviewFinding(finding, findingIndex, globalIndex++, true)
              )}
            </Box>
          );
        }
      }
    }
    
    return allElements;
  };

  // Delete Confirmation Dialog
  const renderDeleteDialog = () => (
    <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
      <DialogTitle>
        {t('admin.reportingManagement.structuredReporting.deleteConfirmation.title')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {Array.isArray(itemToDelete)
            ? t('admin.reportingManagement.structuredReporting.deleteConfirmation.multipleMessage', { count: itemToDelete.length })
            : t('admin.reportingManagement.structuredReporting.deleteConfirmation.singleMessage')
          }
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteDialogOpen(false)} className="button-cancel">
          {t('common.cancel')}
        </Button>
        <Button onClick={handleConfirmDelete} className="button-delete">
          {t('common.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // CSV Import Dialog
  const renderImportDialog = () => (
    <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        CSV Template Import
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <input
            accept=".csv"
            style={{ display: 'none' }}
            id="csv-file-input"
            type="file"
            onChange={handleFileChange}
          />
          <label htmlFor="csv-file-input">
            <Button variant="outlined" component="span" startIcon={<ImportIcon />}>
              CSV-Datei auswählen
            </Button>
          </label>
          
          {importFile && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Datei: {importFile.name}
            </Typography>
          )}

          {importErrors.length > 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Validierungsfehler:</Typography>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {importErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}

          {importPreview && importErrors.length === 0 && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                CSV erfolgreich geparst! Template kann importiert werden.
              </Alert>
              
              <Paper sx={{ p: 2, backgroundColor: (theme) => theme.palette.action.hover }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Vorschau:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Name:</strong> {importPreview.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Typ:</strong> {importPreview.template_type === 'S' ? 'Strukturiert' : 'Freitext'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Standard für:</strong> {importPreview.standard_for?.type || 'all'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Elemente:</strong> {importPreview.fields_json?.length || 0}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setImportDialogOpen(false)} className="button-cancel">
          Abbrechen
        </Button>
        <Button 
          onClick={handleConfirmImport}
          disabled={!importPreview || importErrors.length > 0}
          className="button"
        >
          Importieren
        </Button>
      </DialogActions>
    </Dialog>
  );

  // HTML Import Dialog
  const renderHtmlImportDialog = () => (
    <Dialog open={htmlImportDialogOpen} onClose={() => setHtmlImportDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('admin.reportingManagement.structuredReporting.htmlImportTitle')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('admin.reportingManagement.structuredReporting.htmlImportDescription')}
          </Typography>

          <input
            accept=".html,.htm"
            style={{ display: 'none' }}
            id="html-file-input"
            type="file"
            multiple
            onChange={handleHtmlFileChange}
          />
          <label htmlFor="html-file-input">
            <Button variant="outlined" component="span" startIcon={<ImportIcon />}>
              HTML-Dateien auswählen
            </Button>
          </label>

          {htmlImportFiles.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {htmlImportFiles.length} Datei(en) ausgewählt:
              </Typography>
              <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 200, overflow: 'auto' }}>
                {htmlImportFiles.map((file, index) => (
                  <Typography key={index} variant="body2" sx={{ py: 0.25 }}>
                    {file.name} ({(file.size / 1024).toFixed(1)} KB)
                  </Typography>
                ))}
              </Paper>
            </Box>
          )}

          {htmlImportResults && (
            <Box sx={{ mt: 2 }}>
              {htmlImportResults.results.map((result, index) => (
                <Alert 
                  key={index} 
                  severity={result.success ? 'success' : 'error'} 
                  sx={{ mb: 1 }}
                >
                  <strong>{result.filename}</strong>
                  {result.success 
                    ? ` → "${result.template_name}" (${t('admin.reportingManagement.structuredReporting.htmlImportSections', { count: result.sections_count })})`
                    : ` → ${result.error}`
                  }
                </Alert>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setHtmlImportDialogOpen(false)} className="button-cancel">
          {htmlImportResults ? t('common.close') : t('common.cancel')}
        </Button>
        {!htmlImportResults && (
          <Button 
            onClick={handleConfirmHtmlImport}
            disabled={htmlImportFiles.length === 0 || htmlImportLoading}
            className="button"
          >
            {htmlImportLoading ? <CircularProgress size={20} /> : t('admin.reportingManagement.structuredReporting.htmlImportButton')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={{ p: 3 }}>
      {loading && <CircularProgress />}
      
      {!editorOpen ? renderTemplateList() : null}
      
      {renderTemplateEditor()}
      {renderFieldEditor()}
      {renderStructuredEditor()}
      {renderDeleteDialog()}
      {renderImportDialog()}
      {renderHtmlImportDialog()}

      {/* Preview Dialog - EXAKT wie FreetextReporting */}
      <Dialog open={previewOpen} onClose={closePreview} maxWidth="md" fullWidth>
        <DialogTitle>{t('common.preview')}: {currentTemplate?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ 
            border: '2px solid var(--rd-accent)', 
            borderRadius: 1, 
            p: 3, 
            backgroundColor: (theme) => theme.palette.background.default,
            overflow: 'auto',
            height: '75vh',
            minHeight: '600px'
          }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              {t('common.preview')}
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {renderPreview(true)}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
                  <Button onClick={closePreview} className="button-cancel">
          {t('common.close')}
        </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StructuredReporting;